// ══════════════════════════════════════════════════════════════
// District88 — Supabase Data Service Layer
// All CRUD operations for profiles, posts, listings, etc.
// ══════════════════════════════════════════════════════════════

import { supabase } from '../lib/supabase';
import { isUserSettingsSchemaMismatch } from '../lib/userSettingsSchema';
import { trackCultureMission } from '../lib/cultureMissionTracker';
import { supabaseEdgePost } from '../lib/supabaseEdgeFetch';
import { normalizeUsername } from '../lib/usernameUtils';
import { compressImages } from '../lib/imageCompression';
import type { Profile, Post, Comment, Listing, ListingOffer, Order, Activity, Follow, Drop, Conversation } from '../types/database';
import { ARCHIVED_TAG, filterVisiblePosts, isPostArchived } from '../lib/postArchive';
import { normalizePostTags } from '../lib/normalizePostTags';
import type { StoryHighlightItem } from '../lib/storyHighlights';
import { parseHighlightsFromSectionContent } from '../lib/storyHighlights';
import { dedupeConversationRows, sameParticipantSet, normalizeParticipantIds } from '../lib/conversationParticipants';
import { computeUserInboundResponseAvgMinutes, formatSellerResponseBadge } from '../lib/dmResponseTime';
import {
  buildDmListingOfferMessage,
  buildDmOfferAcceptedMessage,
  buildDmOfferCounterMessage,
  buildDmOfferDeclinedMessage,
  buildDmBuyerAgreedCounterMessage,
  buildDmBuyerDeclinedCounterMessage,
} from '../lib/dmListingOffer';
import { labelForPurchaseWindow } from '../lib/offerPurchaseWindow';

/** PostgREST when `shop_collections` is missing from cache (migration not applied or cache not reloaded). */
function isShopCollectionsSchemaCacheError(err: { message?: string } | null | undefined): boolean {
  const m = String(err?.message || '').toLowerCase();
  return m.includes('shop_collections') && m.includes('schema cache');
}

/** Missing table / stale PostgREST cache — treat as empty data in optional reads. */
function isPostgrestMissingRelationError(err: { message?: string } | null | undefined): boolean {
  const m = String(err?.message || '').toLowerCase();
  return (
    m.includes('schema cache') ||
    (m.includes('could not find') && m.includes('table')) ||
    m.includes('does not exist')
  );
}

// ── Storage (generic image upload for non-product content) ───
// ── Site staff banners (public read, auth write) ──────────────

export interface SiteStaffBannerRow {
  path_prefix: string;
  headline: string;
  body: string;
  cta_label: string;
  cta_href: string;
  active: boolean;
  updated_at: string;
  updated_by: string | null;
  /** Rich CMS: tagline, blogs, spotlight, feature flags (see sitePageConfig). */
  page_config?: Record<string, unknown> | null;
}

export const siteStaffBannerService = {
  async getAll(): Promise<{ data: SiteStaffBannerRow[]; error: Error | null }> {
    try {
      const { data, error } = await supabase.from('site_staff_banners').select('*').order('path_prefix');
      if (error) {
        if (isPostgrestMissingRelationError(error)) return { data: [], error: null };
        return { data: [], error: error as Error };
      }
      return { data: (data || []) as SiteStaffBannerRow[], error: null };
    } catch (e) {
      if (isPostgrestMissingRelationError(e as { message?: string })) return { data: [], error: null };
      return { data: [], error: e as Error };
    }
  },

  async upsert(row: {
    path_prefix: string;
    headline: string;
    body: string;
    cta_label: string;
    cta_href: string;
    active: boolean;
    updated_by?: string;
    page_config?: Record<string, unknown>;
  }): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.from('site_staff_banners').upsert(
        {
          path_prefix: row.path_prefix,
          headline: row.headline,
          body: row.body,
          cta_label: row.cta_label,
          cta_href: row.cta_href,
          active: row.active,
          updated_by: row.updated_by ?? null,
          updated_at: new Date().toISOString(),
          ...(row.page_config !== undefined ? { page_config: row.page_config } : {}),
        },
        { onConflict: 'path_prefix' }
      );
      return { error: error ? (error as Error) : null };
    } catch (e) {
      return { error: e as Error };
    }
  },

  async remove(path_prefix: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.from('site_staff_banners').delete().eq('path_prefix', path_prefix);
      return { error: error ? (error as Error) : null };
    } catch (e) {
      return { error: e as Error };
    }
  },
};

export const storageService = {
  async uploadImage(file: File): Promise<string> {
    const compressed = await compressImages([file]);
    const f = compressed[0] || file;
    const ext = f.name.split('.').pop();
    const path = `uploads/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('posts').upload(path, f);
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from('posts').getPublicUrl(path);
    return publicUrl;
  },
};

// ── Profiles ─────────────────────────────────────────────────

export const profileService = {
  async getById(id: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .single();
    return { data: data as Profile | null, error };
  },

  async getByIds(ids: string[]) {
    const unique = Array.from(new Set(ids.map(String).filter(Boolean)));
    if (unique.length === 0) return { data: [] as Profile[], error: null };
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .in('id', unique);
    return { data: (data || []) as Profile[], error };
  },

  async getByUsername(username: string) {
    const { data: rows, error } = await supabase.rpc('profile_get_by_username', {
      p_username: username,
    });
    if (!error && rows != null) {
      const arr = Array.isArray(rows) ? rows : [rows];
      const first = arr[0] as Profile | undefined;
      return { data: first ?? null, error: null };
    }
    const u = normalizeUsername(username);
    const { data, error: e2 } = await supabase
      .from('profiles')
      .select('*')
      .eq('username', u)
      .maybeSingle();
    return { data: (data as Profile | null) ?? null, error: error ?? e2 };
  },

  async getAll(limit = 20) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: (data || []) as Profile[], error };
  },

  /**
   * Operations / Live ops: pull profiles in pages until empty or maxRows.
   * Staff RLS must allow selecting all profiles.
   */
  async listAllForOperations(opts: { batchSize?: number; maxRows?: number } = {}) {
    const batchSize = Math.min(1000, Math.max(50, opts.batchSize ?? 400));
    const maxRows = Math.min(20000, Math.max(batchSize, opts.maxRows ?? 10000));
    const out: Profile[] = [];
    let offset = 0;
    try {
      while (out.length < maxRows) {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false })
          .range(offset, offset + batchSize - 1);
        if (error) return { data: out, error: error as Error };
        const rows = (data || []) as Profile[];
        out.push(...rows);
        if (rows.length < batchSize) break;
        offset += batchSize;
      }
      return { data: out, error: null };
    } catch (e) {
      return { data: out, error: e as Error };
    }
  },

  async search(query: string, limit = 20) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .or(`username.ilike.%${query}%,display_name.ilike.%${query}%`)
      .limit(limit);
    return { data: (data || []) as Profile[], error };
  },

  async getNewCreators(limit = 10) {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString();
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .gte('created_at', thirtyDaysAgo)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: (data || []) as Profile[], error };
  },

  async update(id: string, updates: Partial<Profile>) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', id)
      .select()
      .maybeSingle();
    if (error) return { data: null, error };
    if (!data) {
      return {
        data: null,
        error: new Error(
          'Profile update returned no row. Either the id is wrong, or Row Level Security blocked the update (e.g. staff needs the profiles staff-update migration).'
        ),
      };
    }
    return { data: data as Profile, error: null };
  },

  async uploadAvatar(userId: string, file: File) {
    const rawExt = (file.name || '').split('.').pop()?.toLowerCase();
    const ext = rawExt && /^[a-z0-9]+$/.test(rawExt) ? rawExt : 'png';
    // Use a unique path to avoid stale CDN/browser caching when users update their photo.
    const path = `avatars/${userId}/${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true });

    if (uploadError) return { url: null, error: uploadError };

    const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);

    const { error: profErr } = await supabase.from('profiles').update({ avatar_url: publicUrl }).eq('id', userId);
    if (profErr) return { url: null, error: profErr };

    return { url: publicUrl, error: null };
  },
};


// ── Posts ─────────────────────────────────────────────────────

export const postService = {
  async getFeed(options: { limit?: number; offset?: number; type?: string; types?: string[] } = {}) {
    const { limit = 20, offset = 0, type, types } = options;

    let query = supabase
      .from('posts')
      .select('*, author:profiles!author_id(*)')
      .eq('visibility', 'public')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (type) query = query.eq('type', type);
    if (!type && Array.isArray(types) && types.length) query = query.in('type', types);

    const { data, error } = await query;
    return { data: filterVisiblePosts((data || []) as (Post & { author: Profile })[]), error };
  },

  /**
   * Viewer-aware feed (respects posts.visibility).
   *
   * - public: everyone
   * - followers: viewer follows author OR viewer is author
   * - friends: mutual follow OR viewer is author
   * - private: viewer is author only
   */
  async getFeedForViewer(
    viewerId: string,
    options: { limit?: number; offset?: number; type?: string; types?: string[] } = {}
  ) {
    const { limit = 20, offset = 0, type, types } = options;

    // Pull a wider window, then filter client-side (simple + avoids complex SQL/RLS drift).
    const cap = Math.min(240, Math.max(limit * 5, 80));

    let query = supabase
      .from('posts')
      .select('*, author:profiles!author_id(*)')
      .in('visibility', ['public', 'followers', 'friends', 'private'])
      .order('created_at', { ascending: false })
      .range(offset, offset + cap - 1);

    if (type) query = query.eq('type', type);
    if (!type && Array.isArray(types) && types.length) query = query.in('type', types);

    const [{ data, error }, followingIds, followers] = await Promise.all([
      query,
      followService.getFollowingIds(viewerId),
      followService.getFollowers(viewerId),
    ]);

    if (error) return { data: [] as (Post & { author: Profile })[], error };

    const following = new Set((followingIds || []).map(String));
    const followerIds = new Set((followers.data || []).map((p) => String(p.id)));
    const mutual = new Set<string>();
    Array.from(following).forEach((id) => {
      if (followerIds.has(id)) mutual.add(id);
    });

    const visible = filterVisiblePosts((data || []) as (Post & { author: Profile })[]).filter((p) => {
      const vis = (p as any)?.visibility ?? 'public';
      if (p.author_id === viewerId) return true;
      if (vis === 'public') return true;
      if (vis === 'private') return false;
      if (vis === 'followers') return following.has(String(p.author_id));
      if (vis === 'friends') return mutual.has(String(p.author_id));
      return false;
    });

    return { data: visible.slice(0, limit), error: null };
  },

  /** Wide pull for Clips / FYP — client filters to media-friendly posts */
  async getClipsCandidatePosts(opts: { limit?: number } = {}) {
    const limit = opts.limit ?? 120;
    const { data, error } = await supabase
      .from('posts')
      .select('*, author:profiles!author_id(*)')
      .eq('visibility', 'public')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: filterVisiblePosts((data || []) as (Post & { author: Profile })[]), error };
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('posts')
      .select('*, author:profiles!author_id(*)')
      .eq('id', id)
      .single();
    return { data: data as (Post & { author: Profile }) | null, error };
  },

  async getByAuthor(authorId: string, limit = 20) {
    const { data, error } = await supabase
      .from('posts')
      .select('*, author:profiles!author_id(*)')
      .eq('author_id', authorId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: (data || []) as (Post & { author: Profile })[], error };
  },

  async create(post: {
    author_id: string;
    content: string;
    type: string;
    images?: string[];
    tags?: string[];
    poll_options?: Record<string, number> | null;
    community_id?: string | null;
    visibility?: 'public' | 'followers' | 'friends' | 'private';
    comments_enabled?: boolean;
    hide_likes?: boolean;
    location?: string | null;
    tagged_people?: string[];
  }) {
    const { data, error } = await supabase
      .from('posts')
      .insert({
        author_id: post.author_id,
        content: post.content,
        type: post.type,
        images: post.images || [],
        tags: post.tags || [],
        poll_options: post.poll_options || null,
        community_id: post.community_id || null,
        visibility: post.visibility ?? 'public',
        comments_enabled: post.comments_enabled ?? true,
        hide_likes: post.hide_likes ?? false,
        location: post.location ?? null,
        tagged_people: post.tagged_people ?? [],
      })
      .select('*, author:profiles!author_id(*)')
      .single();
    return { data: data as (Post & { author: Profile }) | null, error };
  },

  async update(id: string, updates: Partial<Post>) {
    const { data, error } = await supabase
      .from('posts')
      .update(updates)
      .eq('id', id)
      .select('*, author:profiles!author_id(*)')
      .single();
    return { data: data as (Post & { author: Profile }) | null, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('posts').delete().eq('id', id);
    return { error };
  },

  /** Hide post from public feeds (author only) — adds archived tag */
  async archive(id: string, authorId: string) {
    const { data: row, error: fetchErr } = await this.getById(id);
    if (fetchErr || !row) return { error: fetchErr || new Error('Post not found') };
    if (row.author_id !== authorId) return { error: new Error('Only the author can archive this post') };
    const tags = normalizePostTags(row.tags);
    if (isPostArchived(row)) return { data: row, error: null };
    const { data, error } = await this.update(id, { tags: [...tags, ARCHIVED_TAG] } as Partial<Post>);
    return { data, error };
  },

  async uploadImages(files: File[]) {
    const compressed = await compressImages(files);
    const urls: string[] = [];
    for (const file of compressed) {
      const ext = file.name.split('.').pop();
      const path = `posts/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('posts').upload(path, file);
      if (!error) {
        const { data: { publicUrl } } = supabase.storage.from('posts').getPublicUrl(path);
        urls.push(publicUrl);
      }
    }
    return urls;
  },

  async uploadVideo(file: File): Promise<string> {
    const ext = file.name.split('.').pop()?.toLowerCase() || 'mp4';
    const path = `posts/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('posts').upload(path, file, {
      contentType: file.type || undefined,
      cacheControl: '3600',
    });
    if (error) throw error;
    const { data: urlData } = supabase.storage.from('posts').getPublicUrl(path);
    return urlData.publicUrl;
  },
};


// ── Comments ─────────────────────────────────────────────────

export const commentService = {
  async getByPost(postId: string) {
    const { data, error } = await supabase
      .from('comments')
      .select('*, author:profiles!author_id(*)')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });
    return { data: (data || []) as (Comment & { author: Profile })[], error };
  },

  async create(comment: { post_id: string; author_id: string; content: string; parent_id?: string | null }) {
    const { data, error } = await supabase
      .from('comments')
      .insert({
        post_id: comment.post_id,
        author_id: comment.author_id,
        content: comment.content,
        parent_id: comment.parent_id || null,
      })
      .select('*, author:profiles!author_id(*)')
      .single();
    if (comment) {
      void trackCultureMission('leave_comments');
    }
    return { data: data as (Comment & { author: Profile }) | null, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('comments').delete().eq('id', id);
    return { error };
  },
};


// ── Likes / Reactions ────────────────────────────────────────

export const likeService = {
  async toggle(postId: string, userId: string, reactionType = 'like') {
    // Check if already liked
    const { data: existing } = await supabase
      .from('likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', userId)
      .maybeSingle();

    if (existing) {
      // Unlike
      await supabase.from('likes').delete().eq('id', existing.id);
      return { liked: false };
    } else {
      // Like
      await supabase.from('likes').insert({
        post_id: postId,
        user_id: userId,
        reaction_type: reactionType,
      });
      return { liked: true };
    }
  },

  async hasLiked(postId: string, userId: string) {
    const { data } = await supabase
      .from('likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', userId)
      .maybeSingle();
    return !!data;
  },

  async getLikedPostIds(userId: string, postIds: string[]) {
    if (postIds.length === 0) return new Set<string>();
    const { data } = await supabase
      .from('likes')
      .select('post_id')
      .eq('user_id', userId)
      .in('post_id', postIds);
    return new Set((data || []).map(l => l.post_id));
  },
};

// ── Saves (bookmarks) ───────────────────────────────────────

export const saveService = {
  async toggle(postId: string, userId: string) {
    try {
      const { data: existing } = await supabase
        .from('saved_posts')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) {
        await supabase.from('saved_posts').delete().eq('id', existing.id);
        return { saved: false };
      } else {
        await supabase.from('saved_posts').insert({ post_id: postId, user_id: userId });
        return { saved: true };
      }
    } catch { return { saved: false }; }
  },

  async getSavedPostIds(userId: string, postIds: string[]) {
    if (postIds.length === 0) return new Set<string>();
    try {
      const { data } = await supabase
        .from('saved_posts')
        .select('post_id')
        .eq('user_id', userId)
        .in('post_id', postIds);
      return new Set((data || []).map(s => s.post_id));
    } catch { return new Set<string>(); }
  },

  async getByUser(userId: string, limit = 50) {
    try {
      const { data } = await supabase
        .from('saved_posts')
        .select('post_id, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      return data || [];
    } catch { return []; }
  },
};

// ── Reposts ─────────────────────────────────────────────────

export const repostService = {
  async toggle(postId: string, userId: string) {
    try {
      const { data: existing } = await supabase
        .from('reposts')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) {
        await supabase.from('reposts').delete().eq('id', existing.id);
        return { reposted: false };
      } else {
        await supabase.from('reposts').insert({ post_id: postId, user_id: userId });
        return { reposted: true };
      }
    } catch { return { reposted: false }; }
  },

  async getRepostedPostIds(userId: string, postIds: string[]) {
    if (postIds.length === 0) return new Set<string>();
    try {
      const { data } = await supabase
        .from('reposts')
        .select('post_id')
        .eq('user_id', userId)
        .in('post_id', postIds);
      return new Set((data || []).map(r => r.post_id));
    } catch { return new Set<string>(); }
  },

  async getByUser(userId: string, limit = 50) {
    try {
      const { data } = await supabase
        .from('reposts')
        .select('post_id, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      return data || [];
    } catch { return []; }
  },

  async getByUserWithPosts(userId: string, limit = 50) {
    try {
      const { data: repostRows } = await supabase
        .from('reposts')
        .select('post_id, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (!repostRows || repostRows.length === 0) return [];
      const postIds = repostRows.map(r => r.post_id);
      const { data: posts } = await supabase
        .from('posts')
        .select('*, author:profiles!author_id(*)')
        .in('id', postIds);
      return (posts || []) as any[];
    } catch { return []; }
  },
};


// ── Listings (Market) ────────────────────────────────────────

export const listingService = {
  async getAll(options: { limit?: number; offset?: number; status?: string; brand?: string; category?: string; search?: string; sortBy?: string; districtStore?: boolean; collectionSlug?: string } = {}) {
    const { limit = 20, offset = 0, status = 'active', brand, category, search, sortBy = 'created_at', districtStore, collectionSlug } = options;

    let query = supabase
      .from('listings')
      .select('*, seller:profiles!seller_id(*)')
      .eq('status', status)
      .order(sortBy, { ascending: false })
      .range(offset, offset + limit - 1);

    // Omit filter when undefined so "All" market can show peer + District listings.
    if (districtStore === true) {
      query = query.eq('is_district_store', true);
    } else if (districtStore === false) {
      // Peer marketplace only: exclude District-owned rows.
      query = query.or('is_district_store.is.null,is_district_store.eq.false');
    }

    if (brand) query = query.eq('brand', brand);
    if (category) query = query.eq('category', category);
    if (collectionSlug) query = query.contains('shop_collections', [collectionSlug]);
    if (search) query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%,brand.ilike.%${search}%`);

    const { data, error } = await query;

    if (error && collectionSlug && isShopCollectionsSchemaCacheError(error)) {
      const ceiling = Math.min(1000, Math.max(offset + limit * 4, 200));
      let q2 = supabase
        .from('listings')
        .select('*, seller:profiles!seller_id(*)')
        .eq('status', status)
        .order(sortBy, { ascending: false })
        .range(0, ceiling - 1);
      if (districtStore === true) q2 = q2.eq('is_district_store', true);
      else if (districtStore === false) {
        q2 = q2.or('is_district_store.is.null,is_district_store.eq.false');
      }
      if (brand) q2 = q2.eq('brand', brand);
      if (category) q2 = q2.eq('category', category);
      if (search) q2 = q2.or(`title.ilike.%${search}%,description.ilike.%${search}%,brand.ilike.%${search}%`);
      const { data: wide, error: err2 } = await q2;
      if (err2) return { data: [], error: err2 };
      const filtered = (wide || []).filter(
        (row) =>
          Array.isArray((row as Listing).shop_collections) &&
          (row as Listing).shop_collections!.includes(collectionSlug)
      );
      return {
        data: filtered.slice(offset, offset + limit) as (Listing & { seller: Profile })[],
        error: null,
      };
    }

    return { data: (data || []) as (Listing & { seller: Profile })[], error };
  },

  /** Operations center: all statuses, optional title/brand search (staff RLS must allow read). */
  async listForStaff(options: { limit?: number; search?: string } = {}) {
    const { limit = 150, search } = options;
    let query = supabase
      .from('listings')
      .select(
        '*, seller:profiles!seller_id(id, username, display_name, avatar_url, email, created_at, is_seller, verified)'
      )
      .order('created_at', { ascending: false })
      .limit(limit);
    const q = (search || '').trim().replace(/%/g, '');
    if (q) {
      query = query.or(`title.ilike.%${q}%,brand.ilike.%${q}%`);
    }
    const { data, error } = await query;
    return { data: (data || []) as (Listing & { seller: Profile })[], error };
  },

  /**
   * @param onlyActive When true (storefront product page), only returns rows with status active
   * and increments view count. Omit or false for seller/admin/internal lookups (any status, no view bump).
   */
  async getById(id: string, options?: { onlyActive?: boolean }) {
    try {
      await supabase.rpc('release_expired_listing_holds');
    } catch {
      /* optional RPC before migration */
    }

    const onlyActive = options?.onlyActive === true;
    let query = supabase
      .from('listings')
      .select('*, seller:profiles!seller_id(*)')
      .eq('id', id);
    if (onlyActive) query = query.eq('status', 'active');
    const { data, error } = await query.single();

    if (data && onlyActive) {
      try {
        await supabase.rpc('increment_views', { listing_id: id });
      } catch {
        await supabase.from('listings').update({ views: (data.views || 0) + 1 }).eq('id', id);
      }
    }

    return { data: data as (Listing & { seller: Profile }) | null, error };
  },

  async getBySeoSlug(slug: string, options?: { onlyActive?: boolean }) {
    const onlyActive = options?.onlyActive !== false;
    let query = supabase
      .from('listings')
      .select('*, seller:profiles!seller_id(*)')
      .eq('seo_slug', slug);
    if (onlyActive) query = query.eq('status', 'active');
    const { data, error } = await query.maybeSingle();

    if (data) {
      if (onlyActive) {
        try {
          await supabase.rpc('increment_views', { listing_id: data.id });
        } catch {
          await supabase.from('listings').update({ views: (data.views || 0) + 1 }).eq('id', data.id);
        }
      }
      return { data: data as Listing & { seller: Profile }, error: null };
    }

    if (error) return { data: null, error };

    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidRe.test(slug)) {
      return this.getById(slug, { onlyActive });
    }

    return { data: null, error: { message: 'Listing not found' } as Error };
  },

  async getByIds(ids: string[]) {
    if (ids.length === 0) return { data: [] as (Listing & { seller: Profile })[], error: null };
    const { data, error } = await supabase
      .from('listings')
      .select('*, seller:profiles!seller_id(*)')
      .in('id', ids);
    return { data: (data || []) as (Listing & { seller: Profile })[], error };
  },

  async getBySeller(sellerId: string) {
    const { data, error } = await supabase
      .from('listings')
      .select('*, seller:profiles!seller_id(*)')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false });
    return { data: (data || []) as (Listing & { seller: Profile })[], error };
  },

  async create(listing: {
    seller_id: string;
    title: string;
    description?: string;
    price: number;
    original_price?: number;
    condition: string;
    size?: string;
    brand?: string;
    category?: string;
    images?: string[];
    is_auction?: boolean;
    auction_ends_at?: string;
    status?: string;
    available_sizes?: string[];
    quantity?: number;
    shop_collections?: string[];
  }) {
    const { data, error } = await supabase
      .from('listings')
      .insert({
        seller_id: listing.seller_id,
        title: listing.title,
        description: listing.description || '',
        price: listing.price,
        original_price: listing.original_price || null,
        condition: listing.condition || 'DS',
        size: listing.size || null,
        brand: listing.brand || null,
        category: listing.category || null,
        images: listing.images || [],
        is_auction: listing.is_auction || false,
        auction_ends_at: listing.auction_ends_at || null,
        status: listing.status || 'active',
        available_sizes: listing.available_sizes || [],
        quantity: listing.quantity || 1,
        shop_collections: listing.shop_collections?.length ? listing.shop_collections : [],
      })
      .select('*')
      .single();
    return { data: data as Listing | null, error };
  },

  async update(id: string, updates: Partial<Listing>) {
    const { data, error } = await supabase
      .from('listings')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data: data as Listing | null, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('listings').delete().eq('id', id);
    return { error };
  },

  async uploadImages(files: File[]): Promise<{ urls: string[]; errors: string[] }> {
    const compressed = await compressImages(files);
    const urls: string[] = [];
    const errors: string[] = [];
    for (const file of compressed) {
      const ext = file.name.split('.').pop();
      const path = `listings/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('listings').upload(path, file);
      if (error) {
        console.error(`Image upload failed for ${file.name}:`, error.message);
        errors.push(`${file.name}: ${error.message}`);
      } else {
        const { data: { publicUrl } } = supabase.storage.from('listings').getPublicUrl(path);
        urls.push(publicUrl);
      }
    }
    return { urls, errors };
  },

  /**
   * Copies remote http(s) images into District88 Supabase storage when the browser can fetch them (CORS).
   * On failure, keeps the original URL so staff can still publish.
   */
  async mirrorRemoteImages(urls: string[]): Promise<string[]> {
    const out: string[] = [];
    for (const raw of urls) {
      const url = (raw || '').trim();
      if (!/^https?:\/\//i.test(url)) {
        out.push(url);
        continue;
      }
      try {
        const res = await fetch(url, { mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (!blob.size) throw new Error('empty');
        const ct = blob.type || '';
        const ext = ct.includes('png')
          ? 'png'
          : ct.includes('webp')
            ? 'webp'
            : ct.includes('gif')
              ? 'gif'
              : 'jpg';
        const path = `staff-import/${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`;
        const { error } = await supabase.storage.from('listings').upload(path, blob, { upsert: false });
        if (error) throw error;
        const { data: pub } = supabase.storage.from('listings').getPublicUrl(path);
        out.push(pub.publicUrl);
      } catch {
        out.push(url);
      }
    }
    return out;
  },
};

// ── Listing offers (buyer ↔ seller) ─────────────────────────

async function getListingOfferContext(offerId: string) {
  const { data: row, error } = await supabase.from('listing_offers').select('*').eq('id', offerId).maybeSingle();
  if (error || !row) return { offer: null as ListingOffer | null, listing: null as Record<string, unknown> | null, error: error || new Error('Offer not found') };
  const offer = row as ListingOffer;
  const { data: listing } = await supabase
    .from('listings')
    .select('id, title, price, status, seller_id')
    .eq('id', offer.listing_id)
    .maybeSingle();
  return { offer, listing: listing as Record<string, unknown> | null, error: null };
}

async function sendOfferDm(opts: {
  offer: ListingOffer;
  senderId: string;
  recipientId: string;
  content: string;
  notification?: { title: string; message: string; link?: string };
}) {
  const { offer, senderId, recipientId, content, notification } = opts;
  const { data: conv, error: convErr } = await messageService.findOrCreateConversation([offer.buyer_id, offer.seller_id]);
  if (convErr || !conv?.id) {
    return { conversationId: null as string | null, error: convErr || new Error('Could not open conversation') };
  }

  const { error: msgErr } = await messageService.sendMessage(conv.id, senderId, content);
  if (msgErr) {
    return { conversationId: conv.id, error: msgErr };
  }

  if (notification) {
    void notificationService.create({
      user_id: recipientId,
      type: 'offer',
      title: notification.title,
      message: notification.message,
      link: notification.link ?? `/messages/${conv.id}`,
      actor_id: senderId,
    });
  }

  return { conversationId: conv.id, error: null };
}

async function notifySellerOfListingOffer(opts: {
  offer: ListingOffer;
  listingTitle: string;
  listPrice: number;
  buyerNote?: string | null;
}) {
  const { offer, listingTitle, listPrice, buyerNote } = opts;
  const { data: buyerProf } = await supabase
    .from('profiles')
    .select('display_name, username')
    .eq('id', offer.buyer_id)
    .maybeSingle();

  const buyerName = String(buyerProf?.display_name || buyerProf?.username || 'A buyer').trim() || 'A buyer';
  const content = buildDmListingOfferMessage({
    offerId: offer.id,
    listingId: offer.listing_id,
    listingTitle,
    listPrice,
    offerAmount: Number(offer.amount),
    buyerNote: buyerNote || offer.message,
    buyerName,
  });

  return sendOfferDm({
    offer,
    senderId: offer.buyer_id,
    recipientId: offer.seller_id,
    content,
  });
}

async function notifyBuyerOfOfferAccepted(offer: ListingOffer, listingTitle: string, windowMinutes?: number) {
  const { data: sellerProf } = await supabase
    .from('profiles')
    .select('display_name, username')
    .eq('id', offer.seller_id)
    .maybeSingle();
  const sellerName = String(sellerProf?.display_name || sellerProf?.username || 'Seller').trim() || 'Seller';
  const deadlineLabel = offer.purchase_deadline_at
    ? new Date(offer.purchase_deadline_at).toLocaleString()
    : undefined;
  const windowLabel = windowMinutes ? labelForPurchaseWindow(windowMinutes) : undefined;
  const content = buildDmOfferAcceptedMessage({
    offerId: offer.id,
    listingId: offer.listing_id,
    listingTitle,
    offerAmount: Number(offer.amount),
    sellerName,
    deadlineLabel,
    windowLabel,
  });
  return sendOfferDm({
    offer,
    senderId: offer.seller_id,
    recipientId: offer.buyer_id,
    content,
  });
}

async function notifyBuyerOfOfferDeclined(offer: ListingOffer, listingTitle: string) {
  const { data: sellerProf } = await supabase
    .from('profiles')
    .select('display_name, username')
    .eq('id', offer.seller_id)
    .maybeSingle();
  const sellerName = String(sellerProf?.display_name || sellerProf?.username || 'Seller').trim() || 'Seller';
  const content = buildDmOfferDeclinedMessage({
    offerId: offer.id,
    listingId: offer.listing_id,
    listingTitle,
    offerAmount: Number(offer.amount),
    sellerName,
  });
  return sendOfferDm({
    offer,
    senderId: offer.seller_id,
    recipientId: offer.buyer_id,
    content,
  });
}

async function notifyBuyerOfOfferCounter(opts: {
  offer: ListingOffer;
  listingTitle: string;
  previousAmount: number;
  note?: string | null;
}) {
  const { offer, listingTitle, previousAmount, note } = opts;
  const { data: sellerProf } = await supabase
    .from('profiles')
    .select('display_name, username')
    .eq('id', offer.seller_id)
    .maybeSingle();
  const sellerName = String(sellerProf?.display_name || sellerProf?.username || 'Seller').trim() || 'Seller';
  const content = buildDmOfferCounterMessage({
    offerId: offer.id,
    listingId: offer.listing_id,
    listingTitle,
    previousAmount,
    counterAmount: Number(offer.amount),
    sellerName,
    by: 'seller',
    note,
  });
  return sendOfferDm({
    offer,
    senderId: offer.seller_id,
    recipientId: offer.buyer_id,
    content,
    notification: {
      title: 'Counter offer',
      message: `${sellerName} countered at $${Number(offer.amount).toFixed(2)} on ${listingTitle}`,
    },
  });
}

async function notifySellerOfBuyerCounter(opts: {
  offer: ListingOffer;
  listingTitle: string;
  previousAmount: number;
  note?: string | null;
}) {
  const { offer, listingTitle, previousAmount, note } = opts;
  const { data: buyerProf } = await supabase
    .from('profiles')
    .select('display_name, username')
    .eq('id', offer.buyer_id)
    .maybeSingle();
  const buyerName = String(buyerProf?.display_name || buyerProf?.username || 'Buyer').trim() || 'Buyer';
  const content = buildDmOfferCounterMessage({
    offerId: offer.id,
    listingId: offer.listing_id,
    listingTitle,
    previousAmount,
    counterAmount: Number(offer.amount),
    buyerName,
    by: 'buyer',
    note,
  });
  return sendOfferDm({
    offer,
    senderId: offer.buyer_id,
    recipientId: offer.seller_id,
    content,
    notification: {
      title: 'Counter offer',
      message: `${buyerName} countered at $${Number(offer.amount).toFixed(2)} on ${listingTitle}`,
    },
  });
}

async function notifySellerBuyerAgreedToCounter(offer: ListingOffer, listingTitle: string) {
  const { data: buyerProf } = await supabase
    .from('profiles')
    .select('display_name, username')
    .eq('id', offer.buyer_id)
    .maybeSingle();
  const buyerName = String(buyerProf?.display_name || buyerProf?.username || 'Buyer').trim() || 'Buyer';
  const content = buildDmBuyerAgreedCounterMessage({
    offerId: offer.id,
    listingId: offer.listing_id,
    listingTitle,
    offerAmount: Number(offer.amount),
    buyerName,
  });
  return sendOfferDm({
    offer,
    senderId: offer.buyer_id,
    recipientId: offer.seller_id,
    content,
    notification: {
      title: 'Counter accepted',
      message: `${buyerName} accepted your $${Number(offer.amount).toFixed(2)} counter on ${listingTitle} — set purchase window`,
    },
  });
}

async function notifySellerOfBuyerDeclinedCounter(offer: ListingOffer, listingTitle: string) {
  const { data: buyerProf } = await supabase
    .from('profiles')
    .select('display_name, username')
    .eq('id', offer.buyer_id)
    .maybeSingle();
  const buyerName = String(buyerProf?.display_name || buyerProf?.username || 'Buyer').trim() || 'Buyer';
  const content = buildDmBuyerDeclinedCounterMessage({
    offerId: offer.id,
    listingId: offer.listing_id,
    listingTitle,
    offerAmount: Number(offer.amount),
    buyerName,
  });
  return sendOfferDm({
    offer,
    senderId: offer.buyer_id,
    recipientId: offer.seller_id,
    content,
    notification: {
      title: 'Counter declined',
      message: `${buyerName} declined your counter on ${listingTitle}`,
    },
  });
}

export const listingOfferService = {
  async create(opts: { listingId: string; buyerId: string; amount: number; message?: string }) {
    const { data: listing, error: listErr } = await supabase
      .from('listings')
      .select('id, seller_id, price, status, title')
      .eq('id', opts.listingId)
      .maybeSingle();
    if (listErr) return { data: null as ListingOffer | null, error: listErr, conversationId: null as string | null };
    if (!listing) return { data: null, error: new Error('Listing not found'), conversationId: null };
    if (listing.status !== 'active') return { data: null, error: new Error('This listing is not accepting offers'), conversationId: null };
    if (listing.seller_id === opts.buyerId) return { data: null, error: new Error('You cannot make an offer on your own listing'), conversationId: null };
    const amount = Number(opts.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { data: null, error: new Error('Enter a valid offer amount'), conversationId: null };
    const { data, error } = await supabase
      .from('listing_offers')
      .insert({
        listing_id: opts.listingId,
        buyer_id: opts.buyerId,
        seller_id: listing.seller_id,
        amount,
        message: opts.message?.trim() || null,
      })
      .select('*')
      .single();
    if (error || !data) {
      return { data: (data as ListingOffer) || null, error, conversationId: null };
    }

    const offer = data as ListingOffer;
    const { conversationId, error: notifyError } = await notifySellerOfListingOffer({
      offer,
      listingTitle: String(listing.title || 'this listing'),
      listPrice: Number(listing.price),
      buyerNote: opts.message,
    });

    return { data: offer, error: null, conversationId, notifyError };
  },

  async getById(offerId: string) {
    const { data: row, error } = await supabase.from('listing_offers').select('*').eq('id', offerId).maybeSingle();
    if (error || !row) return { data: null as ListingOffer | null, error: error || new Error('Offer not found') };
    const enriched = await enrichListingOffers([row as ListingOffer]);
    return { data: enriched[0] || null, error: null };
  },

  async getBuyerPendingForListing(listingId: string, buyerId: string) {
    const { data, error } = await supabase
      .from('listing_offers')
      .select('*')
      .eq('listing_id', listingId)
      .eq('buyer_id', buyerId)
      .eq('status', 'pending')
      .maybeSingle();
    return { data: (data as ListingOffer) || null, error };
  },

  /** Accepted offer with a future purchase deadline (agreed checkout window). */
  async getBuyerActiveAcceptedOffer(listingId: string, buyerId: string) {
    const { data, error } = await supabase
      .from('listing_offers')
      .select('*')
      .eq('listing_id', listingId)
      .eq('buyer_id', buyerId)
      .eq('status', 'accepted')
      .maybeSingle();
    if (error || !data) return { data: null as ListingOffer | null, error };
    const row = data as ListingOffer;
    const rawDeadline = row.purchase_deadline_at;
    if (rawDeadline) {
      const t = new Date(rawDeadline).getTime();
      if (Number.isFinite(t) && t < Date.now()) {
        return { data: null, error: null };
      }
    }
    return { data: row, error: null };
  },

  async listForBuyer(buyerId: string, limit = 80) {
    const { data: rows, error } = await supabase
      .from('listing_offers')
      .select('*')
      .eq('buyer_id', buyerId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) return { data: [] as ListingOffer[], error };
    const offers = (rows || []) as ListingOffer[];
    return { data: await enrichListingOffers(offers), error: null };
  },

  async listForSeller(sellerId: string, opts: { status?: string; limit?: number } = {}) {
    const lim = opts.limit ?? 80;
    let q = supabase.from('listing_offers').select('*').eq('seller_id', sellerId).order('created_at', { ascending: false }).limit(lim);
    if (opts.status) q = q.eq('status', opts.status);
    const { data: rows, error } = await q;
    if (error) return { data: [] as ListingOffer[], error };
    const offers = (rows || []) as ListingOffer[];
    return { data: await enrichListingOffers(offers), error: null };
  },

  async withdraw(offerId: string) {
    const { data, error } = await supabase
      .from('listing_offers')
      .update({ status: 'withdrawn', updated_at: new Date().toISOString() })
      .eq('id', offerId)
      .eq('status', 'pending')
      .select('*')
      .single();
    return { data: (data as ListingOffer) || null, error };
  },

  async accept(
    offerId: string,
    opts?: { exclusiveHold?: boolean; purchaseWindowHours?: number; purchaseWindowMinutes?: number }
  ) {
    const { offer: existing, error: ctxErr } = await getListingOfferContext(offerId);
    if (ctxErr || !existing) return { data: null as ListingOffer | null, error: ctxErr || new Error('Offer not found'), conversationId: null };
    if (existing.status !== 'pending') return { data: null, error: new Error('This offer is no longer pending'), conversationId: null };
    if (existing.awaiting_party !== 'seller') return { data: null, error: new Error('Waiting for the buyer to respond'), conversationId: null };

    const exclusiveHold = opts?.exclusiveHold !== false;
    const minutesRaw = opts?.purchaseWindowMinutes ?? (opts?.purchaseWindowHours != null ? opts.purchaseWindowHours * 60 : 1440);
    const allowed = [30, 60, 360, 720, 1440];
    const windowM = allowed.includes(minutesRaw) ? minutesRaw : 1440;
    const deadline = new Date(Date.now() + windowM * 60 * 1000).toISOString();
    const { data, error } = await supabase
      .from('listing_offers')
      .update({
        status: 'accepted',
        updated_at: new Date().toISOString(),
        purchase_deadline_at: deadline,
        exclusive_hold: exclusiveHold,
      })
      .eq('id', offerId)
      .eq('status', 'pending')
      .select('*')
      .maybeSingle();
    if (error || !data) return { data: (data as ListingOffer) || null, error, conversationId: null as string | null };

    const offer = data as ListingOffer;
    const { error: holdErr } = await supabase
      .from('listings')
      .update({
        hold_offer_id: offer.id,
        hold_until: deadline,
        hold_buyer_id: offer.buyer_id,
        updated_at: new Date().toISOString(),
      })
      .eq('id', offer.listing_id);

    const { listing } = await getListingOfferContext(offerId);
    const listingTitle = String(listing?.title || 'this listing');
    const { conversationId, error: notifyError } = await notifyBuyerOfOfferAccepted(offer, listingTitle, windowM);
    return { data: offer, error: holdErr, conversationId, notifyError };
  },

  async decline(offerId: string) {
    const { offer: existing, listing, error: ctxErr } = await getListingOfferContext(offerId);
    if (ctxErr || !existing) return { data: null as ListingOffer | null, error: ctxErr || new Error('Offer not found'), conversationId: null };
    if (existing.status !== 'pending') return { data: null, error: new Error('This offer is no longer pending'), conversationId: null };
    if (existing.awaiting_party !== 'seller') return { data: null, error: new Error('Waiting for the buyer to respond'), conversationId: null };

    const { data, error } = await supabase
      .from('listing_offers')
      .update({ status: 'declined', buyer_agreed_at: null, updated_at: new Date().toISOString() })
      .eq('id', offerId)
      .eq('status', 'pending')
      .select('*')
      .single();
    if (error || !data) return { data: (data as ListingOffer) || null, error, conversationId: null };

    const offer = data as ListingOffer;
    const listingTitle = String(listing?.title || 'this listing');
    const { conversationId, error: notifyError } = await notifyBuyerOfOfferDeclined(offer, listingTitle);
    return { data: offer, error: null, conversationId, notifyError };
  },

  async counter(
    offerId: string,
    opts: { sellerId: string; amount: number; message?: string }
  ) {
    const amount = Number(opts.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return { data: null as ListingOffer | null, error: new Error('Enter a valid counter amount'), conversationId: null as string | null };
    }

    const { offer: existing, listing, error: ctxErr } = await getListingOfferContext(offerId);
    if (ctxErr || !existing) return { data: null, error: ctxErr || new Error('Offer not found'), conversationId: null };
    if (existing.seller_id !== opts.sellerId) return { data: null, error: new Error('Only the seller can counter this offer'), conversationId: null };
    if (existing.status !== 'pending') return { data: null, error: new Error('This offer is no longer pending'), conversationId: null };
    if (existing.awaiting_party !== 'seller') return { data: null, error: new Error('Waiting for the buyer to respond'), conversationId: null };

    const previousAmount = Number(existing.amount);
    const { data, error } = await supabase
      .from('listing_offers')
      .update({
        amount,
        message: opts.message?.trim() || existing.message,
        awaiting_party: 'buyer',
        buyer_agreed_at: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', offerId)
      .eq('seller_id', opts.sellerId)
      .eq('status', 'pending')
      .select('*')
      .single();
    if (error || !data) return { data: (data as ListingOffer) || null, error, conversationId: null };

    const offer = data as ListingOffer;
    const listingTitle = String(listing?.title || 'this listing');
    const { conversationId, error: notifyError } = await notifyBuyerOfOfferCounter({
      offer,
      listingTitle,
      previousAmount,
      note: opts.message,
    });
    return { data: offer, error: null, conversationId, notifyError };
  },

  async buyerCounter(
    offerId: string,
    opts: { buyerId: string; amount: number; message?: string }
  ) {
    const amount = Number(opts.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return { data: null as ListingOffer | null, error: new Error('Enter a valid counter amount'), conversationId: null as string | null };
    }

    const { offer: existing, listing, error: ctxErr } = await getListingOfferContext(offerId);
    if (ctxErr || !existing) return { data: null, error: ctxErr || new Error('Offer not found'), conversationId: null };
    if (existing.buyer_id !== opts.buyerId) return { data: null, error: new Error('Only the buyer can counter this offer'), conversationId: null };
    if (existing.status !== 'pending') return { data: null, error: new Error('This offer is no longer pending'), conversationId: null };
    if (existing.awaiting_party !== 'buyer') return { data: null, error: new Error('Waiting for the seller to respond'), conversationId: null };

    const previousAmount = Number(existing.amount);
    const { data, error } = await supabase
      .from('listing_offers')
      .update({
        amount,
        message: opts.message?.trim() || existing.message,
        awaiting_party: 'seller',
        buyer_agreed_at: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', offerId)
      .eq('buyer_id', opts.buyerId)
      .eq('status', 'pending')
      .select('*')
      .single();
    if (error || !data) return { data: (data as ListingOffer) || null, error, conversationId: null };

    const offer = data as ListingOffer;
    const listingTitle = String(listing?.title || 'this listing');
    const { conversationId, error: notifyError } = await notifySellerOfBuyerCounter({
      offer,
      listingTitle,
      previousAmount,
      note: opts.message,
    });
    return { data: offer, error: null, conversationId, notifyError };
  },

  async buyerAgreeToCounter(offerId: string, buyerId: string) {
    const { offer: existing, listing, error: ctxErr } = await getListingOfferContext(offerId);
    if (ctxErr || !existing) return { data: null as ListingOffer | null, error: ctxErr || new Error('Offer not found'), conversationId: null };
    if (existing.buyer_id !== buyerId) return { data: null, error: new Error('Only the buyer can accept this counter'), conversationId: null };
    if (existing.status !== 'pending') return { data: null, error: new Error('This offer is no longer pending'), conversationId: null };
    if (existing.awaiting_party !== 'buyer') return { data: null, error: new Error('No seller counter to accept'), conversationId: null };

    const agreedAt = new Date().toISOString();
    const { data, error } = await supabase
      .from('listing_offers')
      .update({
        buyer_agreed_at: agreedAt,
        awaiting_party: 'seller',
        updated_at: agreedAt,
      })
      .eq('id', offerId)
      .eq('buyer_id', buyerId)
      .eq('status', 'pending')
      .select('*')
      .maybeSingle();
    if (error || !data) return { data: (data as ListingOffer) || null, error, conversationId: null };

    const offer = data as ListingOffer;
    const listingTitle = String(listing?.title || 'this listing');
    const { conversationId, error: notifyError } = await notifySellerBuyerAgreedToCounter(offer, listingTitle);
    return { data: offer, error: null, conversationId, notifyError };
  },

  async buyerDecline(offerId: string, buyerId: string) {
    const { offer: existing, listing, error: ctxErr } = await getListingOfferContext(offerId);
    if (ctxErr || !existing) return { data: null as ListingOffer | null, error: ctxErr || new Error('Offer not found'), conversationId: null };
    if (existing.buyer_id !== buyerId) return { data: null, error: new Error('Only the buyer can decline this counter'), conversationId: null };
    if (existing.status !== 'pending') return { data: null, error: new Error('This offer is no longer pending'), conversationId: null };
    if (existing.awaiting_party !== 'buyer') return { data: null, error: new Error('No seller counter to decline'), conversationId: null };

    const { data, error } = await supabase
      .from('listing_offers')
      .update({ status: 'withdrawn', buyer_agreed_at: null, updated_at: new Date().toISOString() })
      .eq('id', offerId)
      .eq('buyer_id', buyerId)
      .eq('status', 'pending')
      .select('*')
      .single();
    if (error || !data) return { data: (data as ListingOffer) || null, error, conversationId: null };

    const offer = data as ListingOffer;
    const listingTitle = String(listing?.title || 'this listing');
    const { conversationId, error: notifyError } = await notifySellerOfBuyerDeclinedCounter(offer, listingTitle);
    return { data: offer, error: null, conversationId, notifyError };
  },
};

export const listingHoldService = {
  async watch(listingId: string, userId: string) {
    const { data, error } = await supabase
      .from('listing_hold_watchers')
      .upsert(
        { listing_id: listingId, user_id: userId, notified_at: null },
        { onConflict: 'listing_id,user_id' },
      )
      .select('id')
      .single();
    return { data, error };
  },

  async unwatch(listingId: string, userId: string) {
    const { error } = await supabase
      .from('listing_hold_watchers')
      .delete()
      .eq('listing_id', listingId)
      .eq('user_id', userId);
    return { error };
  },

  async isWatching(listingId: string, userId: string) {
    const { data, error } = await supabase
      .from('listing_hold_watchers')
      .select('id')
      .eq('listing_id', listingId)
      .eq('user_id', userId)
      .maybeSingle();
    if (error) return { watching: false, error };
    return { watching: !!data, error: null };
  },

  async releaseExpired() {
    return supabase.rpc('release_expired_listing_holds');
  },
};

async function enrichListingOffers(offers: ListingOffer[]): Promise<ListingOffer[]> {
  if (!offers.length) return [];
  const listingIds = Array.from(new Set(offers.map((o) => o.listing_id)));
  const profileIds = Array.from(new Set(offers.flatMap((o) => [o.buyer_id, o.seller_id])));
  const [{ data: listings }, { data: profiles }] = await Promise.all([
    supabase.from('listings').select('id, title, price, images, size, status, seller_id').in('id', listingIds),
    supabase.from('profiles').select('id, username, display_name, avatar_url').in('id', profileIds),
  ]);
  const lm = new Map((listings || []).map((l: Record<string, unknown>) => [String(l.id), l]));
  const pm = new Map((profiles || []).map((p: Record<string, unknown>) => [String(p.id), p]));
  return offers.map((o) => ({
    ...o,
    listing: lm.get(o.listing_id) as ListingOffer['listing'],
    buyer: pm.get(o.buyer_id) as ListingOffer['buyer'],
    seller: pm.get(o.seller_id) as ListingOffer['seller'],
  }));
}

/** Load `profiles` / `listings` in follow-up queries so order rows always return (avoids fragile PostgREST embeds). */
async function attachOrderRelations(
  rows: Record<string, unknown>[],
  opts: { buyer: boolean; seller: boolean; listing: boolean }
): Promise<Order[]> {
  if (!rows.length) return [];

  const profileIds = new Set<string>();
  if (opts.buyer) {
    for (const r of rows) {
      const id = r.buyer_id;
      if (id) profileIds.add(String(id));
    }
  }
  if (opts.seller) {
    for (const r of rows) {
      const id = r.seller_id;
      if (id) profileIds.add(String(id));
    }
  }

  const listingIds =
    opts.listing
      ? Array.from(new Set(rows.map((r) => r.listing_id).filter((id) => id != null && String(id).trim() !== '').map(String)))
      : [];

  const [profilesRes, listingsRes] = await Promise.all([
    profileIds.size
      ? supabase.from('profiles').select('id, username, display_name, email, avatar_url').in('id', Array.from(profileIds))
      : Promise.resolve({ data: [] as Pick<Profile, 'id' | 'username' | 'display_name' | 'email' | 'avatar_url'>[] }),
    listingIds.length
      ? supabase.from('listings').select('*').in('id', listingIds)
      : Promise.resolve({ data: [] as Listing[] }),
  ]);

  const pMap = new Map((profilesRes.data || []).map((p) => [p.id, p]));
  const lMap = new Map((listingsRes.data || []).map((l) => [l.id, l]));

  return rows.map((raw) => {
    const listingId = raw.listing_id as string | null | undefined;
    const base = raw as unknown as Order;
    return {
      ...base,
      buyer: opts.buyer ? pMap.get(String(raw.buyer_id)) ?? undefined : undefined,
      seller: opts.seller ? pMap.get(String(raw.seller_id)) ?? undefined : undefined,
      listing: opts.listing && listingId ? lMap.get(String(listingId)) ?? undefined : undefined,
    } as Order;
  });
}

// ── Orders ───────────────────────────────────────────────────

function isMissingOrderHistoryRpcError(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null | undefined;
  const code = String(e?.code || '');
  const m = String(e?.message || '').toLowerCase();
  // PostgREST: PGRST202 function not found in schema cache
  if (code === 'PGRST202') return true;
  // Postgres: undefined_function
  if (code === '42883') return true;
  if (m.includes('could not find the function')) return true;
  if (m.includes('does not exist') && m.includes('function')) return true;
  return false;
}

/**
 * When merged list is empty: do not treat a failing direct `orders` SELECT as fatal if the
 * SECURITY DEFINER RPC succeeded — RLS/column drift can break the table path while the RPC
 * returns [] until the webhook inserts the row (right after checkout).
 */
function resolveMergedOrdersError(opts: { mergedLen: number; directErr: unknown; rpcErr: unknown }): unknown | null {
  if (opts.mergedLen > 0) return null;
  const d = opts.directErr;
  const r = opts.rpcErr;
  if (r && !isMissingOrderHistoryRpcError(r)) return r;
  if (d && r) return d;
  if (!r && d) return null;
  if (d) return d;
  return null;
}

export const orderService = {
  async getById(orderId: string) {
    const { data: row, error } = await supabase.from('orders').select('*').eq('id', orderId).maybeSingle();
    if (error) return { data: null, error };
    if (!row) return { data: null, error: null };
    const [enriched] = await attachOrderRelations([row as Record<string, unknown>], {
      buyer: true,
      seller: true,
      listing: true,
    });
    return { data: enriched ?? null, error: null };
  },

  async getByBuyer(buyerId: string) {
    const buyer = String(buyerId || '').trim();
    if (!buyer) return { data: [], error: null };

    /**
     * Merge RPC + direct query. Empty RPC (e.g. auth.uid() edge cases) used to short-circuit and skip
     * the table path entirely — buyers saw no rows even when `orders` had data for their id.
     */
    const [rpc, direct] = await Promise.all([
      supabase.rpc('buyer_order_history'),
      supabase
        .from('orders')
        .select('*')
        .eq('buyer_id', buyer)
        .order('created_at', { ascending: false })
        .limit(2000),
    ]);

    const rpcRows = !rpc.error && Array.isArray(rpc.data) ? (rpc.data as Record<string, unknown>[]) : [];
    const directRows = !direct.error ? ((direct.data || []) as Record<string, unknown>[]) : [];

    const byId = new Map<string, Record<string, unknown>>();
    for (const r of rpcRows) {
      const id = r?.id != null ? String(r.id) : '';
      if (id) byId.set(id, r);
    }
    for (const r of directRows) {
      const id = r?.id != null ? String(r.id) : '';
      if (id && !byId.has(id)) byId.set(id, r);
    }

    const merged = Array.from(byId.values()).sort(
      (a, b) =>
        new Date(String(b.created_at ?? 0)).getTime() - new Date(String(a.created_at ?? 0)).getTime()
    );

    const mergedErr = resolveMergedOrdersError({
      mergedLen: merged.length,
      directErr: direct.error,
      rpcErr: rpc.error,
    });
    if (mergedErr) return { data: [], error: mergedErr };

    const enriched = await attachOrderRelations(merged, {
      buyer: false,
      seller: true,
      listing: true,
    });
    return { data: enriched, error: null };
  },

  async getBySeller(sellerId: string) {
    const seller = String(sellerId || '').trim();
    if (!seller) return { data: [], error: null };

    const [rpc, direct, dropsRes] = await Promise.all([
      supabase.rpc('seller_order_history'),
      supabase
        .from('orders')
        .select('*')
        .eq('seller_id', seller)
        .order('created_at', { ascending: false })
        .limit(2000),
      supabase.from('drops').select('id').eq('created_by', seller),
    ]);

    const dropIds = Array.from(
      new Set((dropsRes.data || []).map((d: { id: string }) => d.id).filter(Boolean))
    ) as string[];

    let dropOrderRows: Record<string, unknown>[] = [];
    if (dropIds.length) {
      const { data: byDrop } = await supabase
        .from('orders')
        .select('*')
        .in('drop_id', dropIds)
        .order('created_at', { ascending: false });
      dropOrderRows = (byDrop || []) as Record<string, unknown>[];
    }

    const byId = new Map<string, Record<string, unknown>>();
    const rpcRows = !rpc.error && Array.isArray(rpc.data) ? (rpc.data as Record<string, unknown>[]) : [];
    const directRows = !direct.error ? ((direct.data || []) as Record<string, unknown>[]) : [];
    for (const r of rpcRows) {
      const id = r?.id != null ? String(r.id) : '';
      if (id) byId.set(id, r);
    }
    for (const r of directRows) {
      const id = r?.id != null ? String(r.id) : '';
      if (id && !byId.has(id)) byId.set(id, r);
    }
    for (const r of dropOrderRows) {
      byId.set(String(r.id), r);
    }
    const merged = Array.from(byId.values()).sort(
      (a, b) =>
        new Date(String(b.created_at)).getTime() - new Date(String(a.created_at)).getTime()
    );

    const mergedErr = resolveMergedOrdersError({
      mergedLen: merged.length,
      directErr: direct.error,
      rpcErr: rpc.error,
    });
    if (mergedErr) return { data: [], error: mergedErr };
    // Drops lookup is auxiliary (New Drops sales); failure must not mask "no rows yet" when order RPC/table paths succeeded.
    if (dropsRes.error && merged.length === 0 && (rpc.error || direct.error)) {
      return { data: [], error: dropsRes.error };
    }

    const enriched = await attachOrderRelations(merged, {
      buyer: true,
      seller: false,
      listing: true,
    });
    return { data: enriched, error: null };
  },

  async create(order: { listing_id: string; buyer_id: string; seller_id: string; price: number; shipping_cost?: number }) {
    const { data, error } = await supabase
      .from('orders')
      .insert({
        listing_id: order.listing_id,
        buyer_id: order.buyer_id,
        seller_id: order.seller_id,
        price: order.price,
        shipping_cost: order.shipping_cost || null,
      })
      .select()
      .single();

    // Mark listing as sold and clear any offer hold
    if (data) {
      await supabase
        .from('listings')
        .update({
          status: 'sold',
          hold_offer_id: null,
          hold_until: null,
          hold_buyer_id: null,
        })
        .eq('id', order.listing_id);
    }

    return { data: data as Order | null, error };
  },

  async updateStatus(id: string, status: string, trackingNumber?: string) {
    const updates: Record<string, any> = { status };
    if (trackingNumber) updates.tracking_number = trackingNumber;
    const { data, error } = await supabase
      .from('orders')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data: data as Order | null, error };
  },
};


// ── Follows ──────────────────────────────────────────────────

export const followService = {
  async toggle(followerId: string, followingId: string) {
    const { data: existing } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', followerId)
      .eq('following_id', followingId)
      .maybeSingle();

    if (existing) {
      await supabase.from('follows').delete().eq('id', existing.id);
      return { following: false };
    } else {
      await supabase.from('follows').insert({
        follower_id: followerId,
        following_id: followingId,
      });
      void trackCultureMission('follow_creators');
      return { following: true };
    }
  },

  async isFollowing(followerId: string, followingId: string) {
    const { data } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', followerId)
      .eq('following_id', followingId)
      .maybeSingle();
    return !!data;
  },

  async getFollowerCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('following_id', userId);
    if (error) {
      console.warn('getFollowerCount:', error.message);
      return 0;
    }
    return count ?? 0;
  },

  async getFollowingCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('follower_id', userId);
    if (error) {
      console.warn('getFollowingCount:', error.message);
      return 0;
    }
    return count ?? 0;
  },

  async getFollowers(userId: string) {
    const { data, error } = await supabase
      .from('follows')
      .select('follower:profiles!follower_id(*)')
      .eq('following_id', userId);
    return { data: (data || []).map(d => (d as any).follower as Profile), error };
  },

  async getFollowing(userId: string) {
    const { data, error } = await supabase
      .from('follows')
      .select('following:profiles!following_id(*)')
      .eq('follower_id', userId);
    return { data: (data || []).map(d => (d as any).following as Profile), error };
  },

  async getFollowingIds(userId: string): Promise<string[]> {
    const { data } = await supabase.from('follows').select('following_id').eq('follower_id', userId);
    return (data || []).map((r: { following_id: string }) => r.following_id);
  },
};


// ── Activities (real-time ticker) ────────────────────────────

export const activityService = {
  async getRecent(limit = 50) {
    const { data, error } = await supabase
      .from('activities')
      .select('*, user:profiles!user_id(*)')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: (data || []) as (Activity & { user: Profile })[], error };
  },

  async create(activity: { type: string; user_id: string; message: string; metadata?: Record<string, any> }) {
    const { data, error } = await supabase
      .from('activities')
      .insert({
        type: activity.type as any,
        user_id: activity.user_id,
        message: activity.message,
        metadata: activity.metadata || null,
      })
      .select()
      .single();
    return { data: data as Activity | null, error };
  },

  // Real-time subscription
  subscribeToNew(callback: (activity: Activity) => void) {
    const channel = supabase
      .channel('activities-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'activities' },
        (payload) => callback(payload.new as Activity)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};


// ── Notifications ────────────────────────────────────────────

export const notificationService = {
  async getForUser(userId: string, limit = 30) {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },

  async getWeeklyDigest() {
    const { data, error } = await supabase.rpc('get_weekly_digest_stats');
    return { data: data as { views: number; offers: number; sales: number; followers: number } | null, error };
  },

  async getByIdForUser(notificationId: string, userId: string) {
    return supabase
      .from('notifications')
      .select('*')
      .eq('id', notificationId)
      .eq('user_id', userId)
      .maybeSingle();
  },

  async getUnreadCount(userId: string) {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false);
    return { count: count || 0, error };
  },

  async markAsRead(notificationId: string) {
    return supabase.from('notifications').update({ read: true }).eq('id', notificationId);
  },

  async markAllAsRead(userId: string) {
    return supabase.from('notifications').update({ read: true }).eq('user_id', userId).eq('read', false);
  },

  async create(notification: { user_id: string; type: string; title: string; message: string; link?: string; actor_id?: string }) {
    const { data, error } = await supabase.rpc('create_notification', {
      p_user_id: notification.user_id,
      p_type: notification.type,
      p_title: notification.title,
      p_message: notification.message,
      p_link: notification.link ?? null,
      p_actor_id: notification.actor_id ?? null,
    });
    return { data: data ? { id: data as string } : null, error };
  },

  subscribeToNew(userId: string, callback: (notification: any) => void) {
    const channel = supabase
      .channel(`notifications-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        (payload) => callback(payload.new)
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  },
};


// ── Messages ─────────────────────────────────────────────────

export const messageService = {
  /**
   * Lists conversations for the signed-in user (RLS limits to participant rows).
   * Do not use `.contains()` on jsonb `participant_ids` — it breaks matching in PostgREST.
   */
  async getConversations(_userId: string) {
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .order('updated_at', { ascending: false });
    return { data: dedupeConversationRows(data || []), error };
  },

  /** Find an existing 1:1 DM or create one (RPC when available, else client dedupe). */
  async findOrCreateConversation(participantIds: string[]) {
    const ids = Array.from(new Set(participantIds.map(String).filter(Boolean))).sort();
    if (ids.length < 2) {
      return { data: null, error: { message: 'At least two participants are required.' } as Error };
    }

    if (ids.length === 2) {
      const { data: rpcId, error: rpcError } = await supabase.rpc('ensure_dm_conversation', {
        p_user_a: ids[0],
        p_user_b: ids[1],
      });
      if (!rpcError && rpcId) {
        const { data: row, error: fetchError } = await supabase
          .from('conversations')
          .select('*')
          .eq('id', rpcId)
          .single();
        if (!fetchError && row) return { data: row, error: null };
      }
    }

    const { data: convos, error: listError } = await this.getConversations(ids[0]);
    if (listError) return { data: null, error: listError };

    const matches = (convos || []).filter((c: Conversation) =>
      sameParticipantSet(c.participant_ids, ids),
    );
    if (matches.length > 0) {
      const best = matches.sort(
        (a: Conversation, b: Conversation) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      )[0];
      return { data: best, error: null };
    }

    return this.createConversation(ids);
  },

  async getMessages(conversationId: string) {
    const { data, error } = await supabase
      .from('messages')
      .select('*, sender:profiles!sender_id(*)')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    return { data: (data || []), error };
  },

  async sendMessage(conversationId: string, senderId: string, content: string) {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: senderId,
        content,
      })
      .select('*, sender:profiles!sender_id(*)')
      .single();

    // Update conversation last_message
    if (data) {
      await supabase.from('conversations').update({
        last_message: content,
        updated_at: new Date().toISOString(),
      }).eq('id', conversationId);
    }

    return { data, error };
  },

  async createConversation(participantIds: string[]) {
    const ids = Array.from(new Set(participantIds.map(String).filter(Boolean))).sort();
    const { data, error } = await supabase
      .from('conversations')
      .insert({ participant_ids: ids })
      .select()
      .single();
    return { data, error };
  },

  // Real-time subscription
  /** Real avg minutes for user to reply in DMs (seller trust + rep score). */
  async getUserDmResponseStats(userId: string, maxMessages = 800) {
    const { data: convos, error: cErr } = await supabase
      .from('conversations')
      .select('id, participant_ids');
    if (cErr || !convos?.length) {
      return { avgMinutes: null as number | null, replyCount: 0, label: 'Not enough messages' };
    }

    const convoIds = convos
      .filter((c: { participant_ids: unknown }) =>
        normalizeParticipantIds(c.participant_ids).includes(userId)
      )
      .map((c: { id: string }) => c.id);

    if (convoIds.length === 0) {
      return { avgMinutes: null, replyCount: 0, label: 'Not enough messages' };
    }

    const { data: msgs, error: mErr } = await supabase
      .from('messages')
      .select('sender_id, created_at, conversation_id')
      .in('conversation_id', convoIds)
      .order('created_at', { ascending: true })
      .limit(maxMessages);

    if (mErr || !msgs?.length) {
      return { avgMinutes: null, replyCount: 0, label: 'Not enough messages' };
    }

    return computeUserInboundResponseAvgMinutes(
      msgs.map((m: { sender_id: string; created_at: string; conversation_id: string }) => ({
        senderId: m.sender_id,
        timestamp: new Date(m.created_at),
        conversationId: m.conversation_id,
      })),
      userId
    );
  },

  subscribeToMessages(conversationId: string, callback: (message: any) => void) {
    const channel = supabase
      .channel(`messages-${conversationId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
        (payload) => callback(payload.new)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  // Typing indicator: returns { unsubscribe, sendTyping }
  subscribeToTyping(
    conversationId: string,
    currentUserId: string,
    onTyping: (userName: string) => void,
    onStopped: () => void
  ): { unsubscribe: () => void; sendTyping: (userName: string) => void } {
    const channel = supabase.channel(`typing-${conversationId}`);
    let clearTimeoutId: ReturnType<typeof setTimeout>;

    channel
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (payload?.userId === currentUserId) return;
        onTyping(payload?.userName || 'Someone');
        clearTimeout(clearTimeoutId);
        clearTimeoutId = setTimeout(onStopped, 3000);
      })
      .subscribe();

    const sendTyping = (userName: string) => {
      channel.send({
        type: 'broadcast',
        event: 'typing',
        payload: { userId: currentUserId, userName },
      });
    };

    return {
      unsubscribe: () => {
        clearTimeout(clearTimeoutId);
        supabase.removeChannel(channel);
      },
      sendTyping,
    };
  },

  async getReactions(conversationId: string) {
    const { data, error } = await supabase
      .from('message_reactions')
      .select('message_id, user_id, emoji')
      .eq('conversation_id', conversationId);
    return { data: data || [], error };
  },

  async toggleReaction(
    messageId: string,
    conversationId: string,
    userId: string,
    emoji: string,
  ) {
    const { data: existing, error: findError } = await supabase
      .from('message_reactions')
      .select('id')
      .eq('message_id', messageId)
      .eq('user_id', userId)
      .eq('emoji', emoji)
      .maybeSingle();

    if (findError) return { added: false, error: findError };

    if (existing?.id) {
      const { error } = await supabase.from('message_reactions').delete().eq('id', existing.id);
      return { added: false, error };
    }

    const { error } = await supabase.from('message_reactions').insert({
      message_id: messageId,
      conversation_id: conversationId,
      user_id: userId,
      emoji,
    });
    return { added: true, error };
  },

  async markConversationRead(conversationId: string) {
    const { error } = await supabase.rpc('mark_dm_messages_read', {
      p_conversation_id: conversationId,
    });
    return { error };
  },

  subscribeToReactions(conversationId: string, callback: () => void) {
    const channel = supabase
      .channel(`reactions-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_reactions',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => callback(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  subscribeToMessageUpdates(conversationId: string, callback: (row: { id: string; read: boolean; sender_id: string }) => void) {
    const channel = supabase
      .channel(`msg-updates-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const row = payload.new as { id: string; read: boolean; sender_id: string };
          callback(row);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};

// ── Stories (24hr) ──────────────────────────────────────────

export const storyService = {
  async getActive() {
    const { data, error } = await supabase
      .from('stories')
      .select('*, author:profiles!author_id(*)')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('stories')
      .select('*, author:profiles!author_id(*)')
      .eq('author_id', userId)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async create(story: {
    author_id: string;
    content?: string;
    image_url?: string;
    type?: 'text' | 'image' | 'video' | string;
    background_color?: string;
  }) {
    const { data, error } = await supabase
      .from('stories')
      .insert(story)
      .select('*, author:profiles!author_id(*)')
      .single();
    return { data, error };
  },

  async markViewed(storyId: string, viewerId: string) {
    await supabase.from('story_views').upsert({ story_id: storyId, viewer_id: viewerId }, { onConflict: 'story_id,viewer_id' });
  },

  async getViewedIds(viewerId: string, storyIds: string[]): Promise<Set<string>> {
    if (storyIds.length === 0) return new Set();
    const { data } = await supabase
      .from('story_views')
      .select('story_id')
      .eq('viewer_id', viewerId)
      .in('story_id', storyIds);
    return new Set((data || []).map(v => v.story_id));
  },

  async delete(storyId: string) {
    return supabase.from('stories').delete().eq('id', storyId);
  },

  async uploadMedia(file: File): Promise<string> {
    const fallbackExt = file.type.startsWith('video/') ? 'mp4' : 'jpg';
    const ext = file.name.split('.').pop()?.toLowerCase() || fallbackExt;
    const path = `stories/${Date.now()}-${Math.random().toString(36).substring(2)}.${ext}`;
    const { error } = await supabase.storage.from('posts').upload(path, file, {
      contentType: file.type || undefined,
      cacheControl: '3600',
    });
    if (error) return '';
    const { data: urlData } = supabase.storage.from('posts').getPublicUrl(path);
    return urlData.publicUrl;
  },

  /** @deprecated Use uploadMedia */
  async uploadImage(file: File): Promise<string> {
    return this.uploadMedia(file);
  },
};

// ── Endorsements ────────────────────────────────────────────

export const endorsementService = {
  async getForUser(userId: string) {
    const { data, error } = await supabase
      .from('endorsements')
      .select('*, endorser:profiles!endorser_id(*)')
      .eq('endorsed_id', userId)
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async create(endorserId: string, endorsedId: string, category: string, message: string) {
    const { data, error } = await supabase
      .from('endorsements')
      .insert({ endorser_id: endorserId, endorsed_id: endorsedId, category, message })
      .select()
      .single();
    return { data, error };
  },

  async delete(endorserId: string, endorsedId: string, category: string) {
    return supabase.from('endorsements').delete().match({ endorser_id: endorserId, endorsed_id: endorsedId, category });
  },
};

// ── Closet / Vault ──────────────────────────────────────

export interface ClosetItemRow {
  id: string;
  user_id: string;
  name: string;
  brand: string | null;
  category: string;
  image_url: string | null;
  purchase_price: number | null;
  current_value: number | null;
  condition: string | null;
  is_grail: boolean;
  acquired_at: string | null;
  wear_count: number | null;
  last_worn: string | null;
  notes: string | null;
  created_at: string;
  story?: string;
  memory_tag?: string;
  never_sell?: boolean;
  is_public?: boolean;
  photos?: string[];
  outfits?: string[];
  condition_history?: { date: string; condition: string; note: string }[];
  legacy_note?: string;
}

export const closetService = {
  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('closet_items')
      .select('*')
      .eq('user_id', userId)
      .order('acquired_at', { ascending: false, nullsFirst: false });
    return { data: (data || []) as ClosetItemRow[], error };
  },

  async getByUserPublic(userId: string) {
    const { data, error } = await supabase
      .from('closet_items')
      .select('*')
      .eq('user_id', userId)
      .eq('is_public', true)
      .order('acquired_at', { ascending: false, nullsFirst: false });
    return { data: (data || []) as ClosetItemRow[], error };
  },

  async create(item: {
    user_id: string;
    name: string;
    brand?: string;
    category?: string;
    image_url?: string;
    purchase_price?: number;
    current_value?: number;
    condition?: string;
    is_grail?: boolean;
    story?: string;
    memory_tag?: string;
    never_sell?: boolean;
    is_public?: boolean;
    photos?: string[];
    outfits?: string[];
    condition_history?: { date: string; condition: string; note: string }[];
    legacy_note?: string;
  }) {
    const row: Record<string, any> = {
      user_id: item.user_id,
      name: item.name,
      brand: item.brand || null,
      category: item.category || 'sneakers',
      image_url: item.image_url || null,
      purchase_price: item.purchase_price ?? null,
      current_value: item.current_value ?? item.purchase_price ?? null,
      condition: item.condition || 'Good',
      is_grail: item.is_grail ?? false,
      notes: item.story || '',
      story: item.story || '',
      memory_tag: item.memory_tag || 'none',
      never_sell: item.never_sell ?? false,
      is_public: item.is_public ?? true,
      photos: item.photos || [],
      outfits: item.outfits || [],
      condition_history: item.condition_history || [],
      legacy_note: item.legacy_note || '',
      acquired_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from('closet_items').insert(row).select().single();
    return { data: data as ClosetItemRow | null, error };
  },

  async update(itemId: string, updates: Record<string, any>) {
    const { data, error } = await supabase.from('closet_items').update(updates).eq('id', itemId).select().single();
    return { data: data as ClosetItemRow | null, error };
  },

  async delete(itemId: string) {
    return supabase.from('closet_items').delete().eq('id', itemId);
  },

  async markWorn(itemId: string) {
    const { data: item } = await supabase.from('closet_items').select('wear_count').eq('id', itemId).single();
    const count = (item as any)?.wear_count ?? 0;
    const { data, error } = await supabase
      .from('closet_items')
      .update({ last_worn: new Date().toISOString(), wear_count: count + 1 })
      .eq('id', itemId)
      .select()
      .single();
    return { data, error };
  },
};

// ── Wishlists / Grail Tracker ──────────────────────────────

export interface WishlistRow {
  id: string;
  user_id: string;
  name: string;
  brand: string | null;
  image_url: string | null;
  max_price: number | null;
  target_price: number | null;
  saved_amount: number | null;
  priority: string | null;
  notes: string | null;
  found: boolean;
  created_at: string;
}

export const wishlistService = {
  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('wishlists')
      .select('*')
      .eq('user_id', userId)
      .eq('found', false)
      .order('created_at', { ascending: false });
    return { data: (data || []) as WishlistRow[], error };
  },

  async create(item: {
    user_id: string;
    name: string;
    brand?: string;
    image_url?: string;
    max_price?: number;
    target_price?: number;
    saved_amount?: number;
    priority?: string;
    notes?: string;
  }) {
    const row = {
      ...item,
      target_price: item.target_price ?? item.max_price ?? null,
      saved_amount: item.saved_amount ?? 0,
      found: false,
    };
    const { data, error } = await supabase.from('wishlists').insert(row).select().single();
    return { data: data as WishlistRow | null, error };
  },

  async update(itemId: string, updates: Record<string, any>) {
    const { data, error } = await supabase.from('wishlists').update(updates).eq('id', itemId).select().single();
    return { data: data as WishlistRow | null, error };
  },

  async delete(itemId: string) {
    return supabase.from('wishlists').delete().eq('id', itemId);
  },
};

// ── Achievements ───────────────────────────────────────────

export const achievementService = {
  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('achievements')
      .select('*')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false });
    return { data: data || [], error };
  },
};

// ── User Settings ──────────────────────────────────────────

export const userSettingsService = {
  async get(userId: string) {
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (error && isUserSettingsSchemaMismatch(error)) {
      return { data: null, error: null };
    }
    return { data, error };
  },

  async upsert(userId: string, settings: Record<string, any>) {
    const { data, error } = await supabase
      .from('user_settings')
      .upsert({ user_id: userId, ...settings, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
      .select()
      .single();
    if (error && isUserSettingsSchemaMismatch(error)) {
      console.warn('[user_settings] Cloud prefs unavailable — run ensure_user_settings_dashboard_prefs.sql in Supabase.');
      return { data: null, error: null };
    }
    return { data, error };
  },
};

// ── Profile Views ──────────────────────────────────────────

export const profileViewService = {
  async log(profileId: string) {
    await supabase.rpc('log_profile_view', { p_profile_id: profileId });
  },

  async getCount(profileId: string) {
    const { count } = await supabase.from('profile_views').select('*', { count: 'exact', head: true }).eq('profile_id', profileId);
    return count || 0;
  },

  async getRecent(profileId: string, limit = 10) {
    const { data } = await supabase
      .from('profile_views')
      .select('*, viewer:profiles!viewer_id(*)')
      .eq('profile_id', profileId)
      .not('viewer_id', 'is', null)
      .order('viewed_at', { ascending: false })
      .limit(limit);
    return data || [];
  },
};

// ── Profile Sections ───────────────────────────────────────

export const profileSectionService = {
  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('profile_sections')
      .select('*')
      .eq('user_id', userId)
      .order('sort_order', { ascending: true });
    return { data: data || [], error };
  },

  async upsert(section: { id?: string; user_id: string; section_type: string; title: string; content: any; sort_order?: number; is_visible?: boolean }) {
    const { data, error } = await supabase
      .from('profile_sections')
      .upsert(section)
      .select()
      .single();
    return { data, error };
  },

  async delete(sectionId: string) {
    return supabase.from('profile_sections').delete().eq('id', sectionId);
  },

  async reorder(userId: string, orderedIds: string[]) {
    const updates = orderedIds.map((id, i) => supabase.from('profile_sections').update({ sort_order: i }).eq('id', id));
    await Promise.all(updates);
  },
};

// ── Story highlights (Instagram-style rings on profile) ─────

export const storyHighlightService = {
  async list(userId: string): Promise<StoryHighlightItem[]> {
    const { data } = await profileSectionService.getByUser(userId);
    const row = (data as { section_type?: string; content?: unknown }[]).find(
      (s) => s.section_type === 'story_highlights',
    );
    return row ? parseHighlightsFromSectionContent(row.content) : [];
  },

  async save(userId: string, highlights: StoryHighlightItem[]) {
    const { data: sections } = await profileSectionService.getByUser(userId);
    const existing = (sections as { id?: string; section_type?: string }[]).find(
      (s) => s.section_type === 'story_highlights',
    );
    return profileSectionService.upsert({
      id: existing?.id,
      user_id: userId,
      section_type: 'story_highlights',
      title: 'Story Highlights',
      content: { highlights },
      is_visible: true,
      sort_order: 0,
    });
  },
};

// ── User Interests ─────────────────────────────────────────

// ── Drops ─────────────────────────────────────────────────

export const dropService = {
  async getAll(opts: { status?: string; limit?: number; offset?: number } = {}) {
    let q = supabase.from('drops').select('*').order('created_at', { ascending: false });
    if (opts.status) q = q.eq('status', opts.status);
    if (opts.limit) q = q.limit(opts.limit);
    if (opts.offset) q = q.range(opts.offset, opts.offset + (opts.limit || 20) - 1);
    const { data, error } = await q;
    return { data: (data || []) as Drop[], error };
  },

  /** Operations / admin: all drops (same as getAll; use for staff dashboards). */
  async listForStaff(options: { limit?: number; status?: string } = {}) {
    const { limit = 250, status } = options;
    let q = supabase.from('drops').select('*').order('created_at', { ascending: false }).limit(limit);
    if (status) q = q.eq('status', status);
    const { data, error } = await q;
    return { data: (data || []) as Drop[], error };
  },

  async getPublished(limit = 50, opts?: { collectionSlug?: string }) {
    const base = () =>
      supabase
        .from('drops')
        .select('*')
        .in('status', ['published', 'featured'])
        .eq('visibility', 'public');

    let q = base();
    if (opts?.collectionSlug) q = q.contains('shop_collections', [opts.collectionSlug]);
    const { data, error } = await q
      .order('release_date', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error && opts?.collectionSlug && isShopCollectionsSchemaCacheError(error)) {
      const cap = Math.min(Math.max(limit * 4, 80), 400);
      const { data: wide, error: err2 } = await base()
        .order('release_date', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(cap);
      if (err2) return { data: [], error: err2 };
      const slug = opts.collectionSlug;
      const filtered = (wide || []).filter(
        (d) => Array.isArray((d as Drop).shop_collections) && (d as Drop).shop_collections!.includes(slug)
      );
      return { data: filtered.slice(0, limit) as Drop[], error: null };
    }

    return { data: (data || []) as Drop[], error };
  },

  /** Culture hub: only show true culture radar releases (exclude URL-imported products). */
  async getCulturePublished(limit = 50, opts?: { collectionSlug?: string }) {
    const { data, error } = await this.getPublished(limit, opts);
    if (error) return { data: [] as Drop[], error };
    return {
      data: (data || []).filter((d) => (d.drop_kind ?? 'culture') !== 'imported'),
      error: null,
    };
  },

  async getForRadar(limit = 100) {
    const { data, error } = await supabase
      .from('drops')
      .select('*')
      .in('status', ['published', 'featured'])
      .eq('visibility', 'public')
      .order('release_date', { ascending: true, nullsFirst: true })
      .limit(limit);
    return { data: (data || []) as Drop[], error };
  },

  async getFeatured() {
    const { data, error } = await supabase
      .from('drops')
      .select('*')
      .eq('status', 'featured')
      .eq('visibility', 'public')
      .order('created_at', { ascending: false });
    return { data: (data || []) as Drop[], error };
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('drops')
      .select('*')
      .eq('id', id)
      .single();
    return { data: data as Drop | null, error };
  },

  async create(drop: Partial<Drop>) {
    const { data, error } = await supabase.from('drops').insert(drop).select().single();
    return { data: data as Drop | null, error };
  },

  async update(id: string, updates: Partial<Drop>) {
    const { data, error } = await supabase.from('drops').update(updates).eq('id', id).select().single();
    return { data: data as Drop | null, error };
  },

  async delete(id: string) {
    return supabase.from('drops').delete().eq('id', id);
  },

  async incrementViews(id: string) {
    const { error: rpcErr } = await supabase.rpc('increment_drop_views', { drop_id: id });
    if (!rpcErr) return;
    const { data: row } = await supabase.from('drops').select('views').eq('id', id).maybeSingle();
    const v = Number((row as { views?: unknown } | null)?.views);
    const base = Number.isFinite(v) ? v : 0;
    await supabase.from('drops').update({ views: base + 1 }).eq('id', id);
  },

  async scrapeUrl(url: string) {
    // Client-side parsing for GOAT, StockX & Flight Club (no server needed)
    const { parseProductFromUrl } = await import('../lib/productUrlParser');
    const parsed = parseProductFromUrl(url);
    if (parsed) return { data: parsed, error: null };

    // Fallback: try scrape-product Edge Function for other URLs
    const { data: { session } } = await supabase.auth.getSession();
    const base = process.env.REACT_APP_SUPABASE_URL;
    if (!base) return { data: null, error: 'Supabase URL not configured' };
    const res = await fetch(`${base}/functions/v1/scrape-product`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ url }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Scrape failed' }));
      return {
        data: null,
        error: err.error || 'Paste a GOAT, StockX, or Flight Club URL to auto-fill (or use Edge scrape for other links)',
      };
    }
    const data = await res.json();
    return { data, error: null };
  },

  /**
   * Server-side StockX import (Edge Function `import-stockx`).
   * Product URLs → draft drop. Collection URLs → agent_import_queue.
   */
  async importFromStockx(
    url: string,
    options: { action?: 'draft' | 'queue' | 'autofill' | 'full' | 'auto_import' } = {},
  ): Promise<{
    data: {
      action: string;
      dropId?: string;
      queueJobId?: string;
      title?: string;
      message?: string;
      parsed?: Record<string, unknown>;
    } | null;
    error: string | null;
  }> {
    const action = options.action ?? 'draft';
    const edgeAction =
      action === 'full' ? 'auto_import' : action === 'auto_import' ? 'auto_import' : action;
    const { data, error } = await supabaseEdgePost<{
      action: string;
      dropId?: string;
      queueJobId?: string;
      title?: string;
      message?: string;
      parsed?: Record<string, unknown>;
    }>('import-stockx', { url: url.trim(), action: edgeAction });

    if (error || !data) return { data: null, error: error || 'Import failed' };
    return { data, error: null };
  },
};

// ── Drop Watchlist ────────────────────────────────────────

export const dropWatchlistService = {
  /** Row count for this drop (works even when `drops.watchlist_count` is stale). */
  async countForDrop(dropId: string): Promise<number> {
    const { count, error } = await supabase
      .from('drop_watchlist')
      .select('*', { count: 'exact', head: true })
      .eq('drop_id', dropId);
    if (error) return 0;
    return count ?? 0;
  },

  async isWatching(userId: string, dropId: string) {
    const { data } = await supabase
      .from('drop_watchlist')
      .select('id, notify')
      .eq('user_id', userId)
      .eq('drop_id', dropId)
      .maybeSingle();
    return { watching: !!data, notify: data?.notify ?? false, id: data?.id };
  },

  async add(userId: string, dropId: string, notify = true) {
    const { error } = await supabase.from('drop_watchlist').insert({ user_id: userId, drop_id: dropId, notify });
    if (!error) {
      try { await supabase.rpc('increment_drop_watchlist', { did: dropId }); } catch {}
    }
    return { error };
  },

  async remove(userId: string, dropId: string) {
    const { error } = await supabase.from('drop_watchlist').delete().eq('user_id', userId).eq('drop_id', dropId);
    return { error };
  },

  async toggleNotify(userId: string, dropId: string, notify: boolean) {
    return supabase.from('drop_watchlist').update({ notify }).eq('user_id', userId).eq('drop_id', dropId);
  },

  async getUserWatchlist(userId: string) {
    const { data, error } = await supabase
      .from('drop_watchlist')
      .select('*, drops(*)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },
};

export const userInterestService = {
  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('user_interests')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async add(userId: string, category: string, value: string) {
    return supabase.from('user_interests').upsert({ user_id: userId, category, value }, { onConflict: 'user_id,category,value' });
  },

  async remove(userId: string, category: string, value: string) {
    return supabase.from('user_interests').delete().match({ user_id: userId, category, value });
  },
};

// ── Seller Rep Scores ────────────────────────────────────────

export interface SellerRepScore {
  id: string;
  seller_id: string;
  sales_score: number;
  review_score: number;
  response_time_score: number;
  shipping_speed_score: number;
  completion_rate_score: number;
  account_age_score: number;
  verification_bonus: number;
  total_sales: number;
  total_reviews: number;
  review_avg: number;
  avg_response_minutes: number;
  avg_ship_hours: number;
  orders_completed: number;
  orders_disputed: number;
  orders_cancelled: number;
  overall_score: number;
  tier: string;
  created_at: string;
  updated_at: string;
}

export interface SellerReview {
  id: string;
  order_id: string | null;
  seller_id: string;
  reviewer_id: string;
  rating: number;
  title: string;
  content: string;
  communication_rating: number | null;
  shipping_rating: number | null;
  accuracy_rating: number | null;
  created_at: string;
}

function computeTier(score: number): string {
  if (score >= 90) return 'Diamond';
  if (score >= 75) return 'Platinum';
  if (score >= 55) return 'Gold';
  if (score >= 30) return 'Silver';
  return 'Bronze';
}

export const repScoreService = {
  async getBySellerID(sellerId: string): Promise<{ data: SellerRepScore | null; error: any }> {
    const { data, error } = await supabase
      .from('seller_rep_scores')
      .select('*')
      .eq('seller_id', sellerId)
      .maybeSingle();
    return { data: data as SellerRepScore | null, error };
  },

  /** Real DM-based response label for seller badges (cached rep score, else live). */
  async getResponseLabel(sellerId: string): Promise<string | null> {
    const { data: rep } = await this.getBySellerID(sellerId);
    if (rep?.avg_response_minutes && rep.avg_response_minutes > 0) {
      const label = formatSellerResponseBadge(rep.avg_response_minutes, 1);
      if (label) return label;
    }
    const stats = await messageService.getUserDmResponseStats(sellerId);
    return formatSellerResponseBadge(stats.avgMinutes, stats.replyCount);
  },

  async recalculate(sellerId: string): Promise<{ data: SellerRepScore | null; error: any }> {
    const [ordersRes, reviewsRes, dmResponseRes, profileRes] = await Promise.all([
      supabase.from('orders').select('*').or(`seller_id.eq.${sellerId},buyer_id.eq.${sellerId}`),
      supabase.from('seller_reviews').select('*').eq('seller_id', sellerId),
      messageService.getUserDmResponseStats(sellerId),
      supabase.from('profiles').select('created_at, verified').eq('id', sellerId).single(),
    ]);

    const orders = ordersRes.data || [];
    const reviews = reviewsRes.data || [];
    const profile = profileRes.data;

    const sellerOrders = orders.filter((o: any) => o.seller_id === sellerId);
    const completedOrders = sellerOrders.filter((o: any) => o.status === 'completed');
    const disputedOrders = sellerOrders.filter((o: any) => o.status === 'disputed');
    const totalSellerOrders = sellerOrders.length;

    // Sales score: 0-20 (logarithmic scale)
    const totalSales = completedOrders.length;
    const salesScore = Math.min(20, Math.round(Math.log2(totalSales + 1) * 5));

    // Review score: 0-25 (based on avg rating)
    const totalReviews = reviews.length;
    const reviewAvg = totalReviews > 0
      ? reviews.reduce((sum: number, r: any) => sum + r.rating, 0) / totalReviews
      : 0;
    const reviewScore = Math.min(25, Math.round(reviewAvg * 5));

    // Response time score: 0-15 (real DM reply gaps, not mock)
    const dmStats = dmResponseRes;
    const avgResponseMinutes =
      dmStats.avgMinutes != null ? Math.round(dmStats.avgMinutes) : 0;
    let responseTimeScore = 0;
    if (avgResponseMinutes <= 60) responseTimeScore = 15;
    else if (avgResponseMinutes <= 240) responseTimeScore = 12;
    else if (avgResponseMinutes <= 720) responseTimeScore = 8;
    else if (avgResponseMinutes <= 1440) responseTimeScore = 5;
    else responseTimeScore = 2;
    if (avgResponseMinutes === 0) responseTimeScore = 5;

    // Shipping speed score: 0-15 (from order created to shipped)
    const shippedOrders = sellerOrders.filter((o: any) => o.tracking_number);
    let avgShipHours = 0;
    let shippingSpeedScore = 5;
    if (shippedOrders.length > 0) {
      const shipTimes = shippedOrders.map((o: any) => {
        const created = new Date(o.created_at).getTime();
        const shipped = new Date(o.updated_at).getTime();
        return Math.max(0, (shipped - created) / 3600000);
      });
      avgShipHours = Math.round(shipTimes.reduce((a: number, b: number) => a + b, 0) / shipTimes.length);
      if (avgShipHours <= 24) shippingSpeedScore = 15;
      else if (avgShipHours <= 48) shippingSpeedScore = 12;
      else if (avgShipHours <= 72) shippingSpeedScore = 8;
      else shippingSpeedScore = 3;
    }

    // Completion rate score: 0-15
    const completionRate = totalSellerOrders > 0 ? completedOrders.length / totalSellerOrders : 0;
    const completionRateScore = Math.min(15, Math.round(completionRate * 15));

    // Account age score: 0-5 (1pt per 3 months)
    let accountAgeScore = 0;
    if (profile?.created_at) {
      const months = (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24 * 30);
      accountAgeScore = Math.min(5, Math.floor(months / 3));
    }

    // Verification bonus: 0-5
    const verificationBonus = profile?.verified ? 5 : 0;

    const overallScore = Math.min(100,
      salesScore + reviewScore + responseTimeScore +
      shippingSpeedScore + completionRateScore + accountAgeScore + verificationBonus
    );

    const tier = computeTier(overallScore);

    const scoreData = {
      seller_id: sellerId,
      sales_score: salesScore,
      review_score: reviewScore,
      response_time_score: responseTimeScore,
      shipping_speed_score: shippingSpeedScore,
      completion_rate_score: completionRateScore,
      account_age_score: accountAgeScore,
      verification_bonus: verificationBonus,
      total_sales: totalSales,
      total_reviews: totalReviews,
      review_avg: Math.round(reviewAvg * 10) / 10,
      avg_response_minutes: avgResponseMinutes,
      avg_ship_hours: avgShipHours,
      orders_completed: completedOrders.length,
      orders_disputed: disputedOrders.length,
      orders_cancelled: sellerOrders.filter((o: any) => o.status === 'cancelled').length,
      overall_score: overallScore,
      tier,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('seller_rep_scores')
      .upsert(scoreData, { onConflict: 'seller_id' })
      .select('*')
      .single();

    return { data: data as SellerRepScore | null, error };
  },

  async getReviews(sellerId: string, limit = 20): Promise<{ data: any[]; error: any }> {
    const { data, error } = await supabase
      .from('seller_reviews')
      .select('*, reviewer:profiles!seller_reviews_reviewer_id_fkey(username, display_name, avatar_url)')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },

  async submitReview(review: {
    order_id?: string;
    seller_id: string;
    reviewer_id: string;
    rating: number;
    title?: string;
    content?: string;
    communication_rating?: number;
    shipping_rating?: number;
    accuracy_rating?: number;
  }) {
    const { data, error } = await supabase
      .from('seller_reviews')
      .insert(review)
      .select('*')
      .single();

    if (!error && data) {
      await supabase.from('rep_events').insert({
        seller_id: review.seller_id,
        event_type: 'review_received',
        points_delta: review.rating >= 4 ? 3 : review.rating >= 3 ? 1 : -2,
        metadata: { rating: review.rating, review_id: data.id },
      });
      await repScoreService.recalculate(review.seller_id);
    }

    return { data, error };
  },

  async logEvent(sellerId: string, eventType: string, pointsDelta: number, metadata?: any) {
    await supabase.from('rep_events').insert({
      seller_id: sellerId,
      event_type: eventType,
      points_delta: pointsDelta,
      metadata: metadata || {},
    });
  },

  async getEventHistory(sellerId: string, limit = 50) {
    const { data, error } = await supabase
      .from('rep_events')
      .select('*')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },
};

// ── Reports / Admin ───────────────────────────────────────

export const reportService = {
  /**
   * @param status `PENDING` | `RESOLVED` | `DISMISSED` | `all` / omit = no filter.
   * Use `closed` to fetch both resolved and dismissed rows (moderation history).
   */
  async getAll(status?: string, limit = 50) {
    let query = supabase
      .from('reports')
      .select('*, reporter:profiles!reporter_id(username, display_name, avatar_url)')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (status && status !== 'all') {
      const u = status.toUpperCase();
      if (u === 'CLOSED') query = query.in('status', ['RESOLVED', 'DISMISSED']);
      else query = query.eq('status', u);
    }
    const { data, error } = await query;
    return { data: data || [], error };
  },

  async updateStatus(reportId: string, status: string, resolution?: string) {
    const updates: Record<string, any> = { status: status.toUpperCase() };
    if (resolution) updates.resolution = resolution;
    if (status === 'RESOLVED' || status === 'DISMISSED') updates.resolved_at = new Date().toISOString();
    return supabase.from('reports').update(updates).eq('id', reportId);
  },

  async addNote(reportId: string, adminId: string, content: string) {
    return supabase.from('admin_notes').insert({
      admin_id: adminId,
      target_type: 'report',
      target_id: reportId,
      content,
    });
  },

  async getNotesForReport(reportId: string) {
    const { data, error } = await supabase
      .from('admin_notes')
      .select('*')
      .eq('target_type', 'report')
      .eq('target_id', reportId)
      .order('created_at', { ascending: true });
    return { data: data || [], error };
  },

  async getStats() {
    const [pending, total, resolved] = await Promise.all([
      supabase.from('reports').select('*', { count: 'exact', head: true }).eq('status', 'PENDING'),
      supabase.from('reports').select('*', { count: 'exact', head: true }),
      supabase.from('reports').select('*', { count: 'exact', head: true }).in('status', ['RESOLVED', 'DISMISSED']),
    ]);
    return { pending: pending.count || 0, total: total.count || 0, resolved: resolved.count || 0 };
  },
};

// ── Employee Activity (tracks who made what change) ───────

export const employeeActivityService = {
  async log(activity: { userId: string; action: string; entityType?: string; entityId?: string; details?: Record<string, any> }) {
    const { error } = await supabase.from('employee_activity').insert({
      user_id: activity.userId,
      action: activity.action,
      entity_type: activity.entityType || null,
      entity_id: activity.entityId || null,
      details: activity.details || {},
    });
    return { error };
  },

  async getRecent(limit = 50) {
    const { data, error } = await supabase
      .from('employee_activity')
      .select('*, user:profiles!user_id(display_name, username, email)')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },

  async getByUser(userId: string, limit = 30) {
    const { data, error } = await supabase
      .from('employee_activity')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },
};

export const auditLogService = {
  async getRecent(limit = 30) {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*, admin:admin_users(display_name)')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },
};

// ── Platform Stats (Admin) ────────────────────────────────

export const platformStatsService = {
  async getOverview() {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const weekAgo = new Date(now.getTime() - 7 * 86400000).toISOString();

    const { data: rpcData, error: rpcError } = await supabase.rpc('operations_get_pulse_stats');
    if (!rpcError && rpcData && typeof rpcData === 'object' && rpcData !== null && 'weekly_orders' in rpcData) {
      const j = rpcData as Record<string, unknown>;
      return {
        totalUsers: Number(j.total_users) || 0,
        activeListings: Number(j.active_listings) || 0,
        pendingReports: Number(j.pending_reports) || 0,
        todayActivity: Number(j.today_activity) || 0,
        weeklyOrders: Number(j.weekly_orders) || 0,
        salesVolume: Number(j.sales_volume) || 0,
      };
    }

    const [users, listings, orders, reports, activities] = await Promise.all([
      supabase.from('profiles').select('*', { count: 'exact', head: true }),
      supabase.from('listings').select('*', { count: 'exact', head: true }).eq('status', 'active'),
      supabase.from('orders').select('price').gte('created_at', weekAgo),
      supabase.from('reports').select('*', { count: 'exact', head: true }).eq('status', 'PENDING'),
      supabase.from('activities').select('*', { count: 'exact', head: true }).gte('created_at', todayStart),
    ]);

    const salesVolume = (orders.data || []).reduce((sum: number, o: any) => sum + Number(o.price || 0), 0);

    return {
      totalUsers: users.count || 0,
      activeListings: listings.count || 0,
      salesVolume,
      pendingReports: reports.count || 0,
      todayActivity: activities.count || 0,
      weeklyOrders: (orders.data || []).length,
    };
  },
};

// ── Seller Reviews (for satisfaction metrics) ─────────────

export const sellerReviewService = {
  async getForSeller(sellerId: string, limit = 20) {
    const { data, error } = await supabase
      .from('seller_reviews')
      .select('*, reviewer:profiles!reviewer_id(username, display_name, avatar_url)')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },

  async getAverages(sellerId: string) {
    const { data, error } = await supabase
      .from('seller_reviews')
      .select('rating, communication_rating, shipping_rating, accuracy_rating')
      .eq('seller_id', sellerId);
    if (!data || data.length === 0) return { avg: 0, communication: 0, shipping: 0, accuracy: 0, count: 0 };
    const count = data.length;
    const avg = (field: string) => data.reduce((s: number, r: any) => s + (Number(r[field]) || 0), 0) / count;
    return { avg: avg('rating'), communication: avg('communication_rating'), shipping: avg('shipping_rating'), accuracy: avg('accuracy_rating'), count };
  },
};

// ── Listing price history (for price confidence) ──────────

export const priceHistoryService = {
  async getSimilarSales(brand: string | null, condition: string, limit = 10) {
    let query = supabase
      .from('orders')
      .select('price, created_at, listing:listings(title, brand, condition, size)')
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(limit);
    const { data, error } = await query;
    return { data: data || [], error };
  },

  async getAvgPrice(brand: string | null, condition: string) {
    const { data } = await supabase
      .from('orders')
      .select('price')
      .eq('status', 'completed');
    if (!data || data.length === 0) return { avg: 0, count: 0 };
    const avg = data.reduce((s: number, o: any) => s + Number(o.price || 0), 0) / data.length;
    return { avg: Math.round(avg), count: data.length };
  },
};

// ── Mutual follows (for profile connections) ──────────────

export const mutualConnectionService = {
  async getMutual(userId: string, limit = 20) {
    const { data: followers } = await supabase
      .from('follows')
      .select('follower_id, follower:profiles!follower_id(id, username, display_name, avatar_url)')
      .eq('following_id', userId)
      .limit(limit);
    return { data: (followers || []).map((f: any) => f.follower).filter(Boolean) };
  },
};

// ── Lab (District Lab) ────────────────────────────────────

export const labService = {
  async getInsights(limit = 20) {
    const { data, error } = await supabase.from('lab_insights').select('*').order('created_at', { ascending: false }).limit(limit);
    return { data: data || [], error };
  },
  async getTrendItems(limit = 50) {
    const { data, error } = await supabase.from('lab_trend_items').select('*').order('momentum', { ascending: false }).limit(limit);
    return { data: data || [], error };
  },
  async getConfig(key: string) {
    const { data, error } = await supabase.from('lab_config').select('data').eq('key', key).single();
    return { data: data?.data ?? [], error };
  },
  async createInsight(item: { title: string; body: string; type?: string; color_class?: string }) {
    const { data, error } = await supabase.from('lab_insights').insert(item).select().single();
    return { data, error };
  },
  async updateInsight(id: string, updates: Record<string, any>) {
    return supabase.from('lab_insights').update(updates).eq('id', id);
  },
  async deleteInsight(id: string) {
    return supabase.from('lab_insights').delete().eq('id', id);
  },
  async createTrendItem(item: Record<string, any>) {
    const { data, error } = await supabase.from('lab_trend_items').insert(item).select().single();
    return { data, error };
  },
  async updateTrendItem(id: string, updates: Record<string, any>) {
    return supabase.from('lab_trend_items').update(updates).eq('id', id);
  },
  async deleteTrendItem(id: string) {
    return supabase.from('lab_trend_items').delete().eq('id', id);
  },
  async updateConfig(key: string, data: unknown) {
    return supabase.from('lab_config').upsert({ key, data, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  },
};

// ── Heart Features (Emotional Hub) ─────────────────────────

export const heartFeaturesService = {
  async getAll() {
    const { data, error } = await supabase.from('heart_features').select('*').order('sort_order', { ascending: true });
    return { data: data || [], error };
  },
  async create(item: { title: string; link_to: string; description?: string; tag?: string; color?: string; icon?: string; sort_order?: number }) {
    const { data, error } = await supabase.from('heart_features').insert(item).select().single();
    return { data, error };
  },
  async update(id: string, updates: Record<string, any>) {
    return supabase.from('heart_features').update(updates).eq('id', id);
  },
  async delete(id: string) {
    return supabase.from('heart_features').delete().eq('id', id);
  },
};

// ── Learn page settings (JSON copy for /learn) ────────────────

export const learnPageSettingsService = {
  async get(): Promise<{ data: { id: string; settings: Record<string, unknown> } | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.from('learn_page_settings').select('*').eq('id', 'learn').maybeSingle();
      if (error) return { data: null, error: error as Error };
      return {
        data: data ? { id: String((data as { id: string }).id), settings: ((data as { settings?: unknown }).settings as Record<string, unknown>) || {} } : null,
        error: null,
      };
    } catch (e) {
      return { data: null, error: e as Error };
    }
  },

  async save(settings: Record<string, unknown>): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.from('learn_page_settings').upsert(
        { id: 'learn', settings, updated_at: new Date().toISOString() },
        { onConflict: 'id' }
      );
      return { error: error ? (error as Error) : null };
    } catch (e) {
      return { error: e as Error };
    }
  },
};

// ── Learn courses (/learn marketplace + featured) ────────────

export interface LearnCourseRow {
  id: string;
  slug: string;
  title: string;
  description: string;
  instructor: string;
  thumbnail_url: string;
  cta_href: string;
  modules_count: number;
  duration_label: string;
  rating: number;
  price_label: string;
  featured: boolean;
  published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const learnCourseService = {
  async getPublished(): Promise<{ data: LearnCourseRow[]; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('learn_courses')
        .select('*')
        .eq('published', true)
        .order('sort_order', { ascending: true });
      if (error) return { data: [], error: error as Error };
      return { data: (data || []) as LearnCourseRow[], error: null };
    } catch (e) {
      return { data: [], error: e as Error };
    }
  },

  async getAllAdmin(): Promise<{ data: LearnCourseRow[]; error: Error | null }> {
    try {
      const { data, error } = await supabase.from('learn_courses').select('*').order('sort_order', { ascending: true });
      if (error) return { data: [], error: error as Error };
      return { data: (data || []) as LearnCourseRow[], error: null };
    } catch (e) {
      return { data: [], error: e as Error };
    }
  },

  async create(
    row: Partial<LearnCourseRow> & Pick<LearnCourseRow, 'slug' | 'title'>
  ): Promise<{ data: LearnCourseRow | null; error: Error | null }> {
    try {
      const payload = {
        slug: row.slug,
        title: row.title,
        description: row.description ?? '',
        instructor: row.instructor ?? '',
        thumbnail_url: row.thumbnail_url ?? '',
        cta_href: row.cta_href ?? '',
        modules_count: row.modules_count ?? 0,
        duration_label: row.duration_label ?? '',
        rating: row.rating ?? 0,
        price_label: row.price_label ?? '',
        featured: row.featured ?? false,
        published: row.published ?? true,
        sort_order: row.sort_order ?? 0,
      };
      const { data, error } = await supabase.from('learn_courses').insert(payload).select().single();
      if (error) return { data: null, error: error as Error };
      return { data: data as LearnCourseRow, error: null };
    } catch (e) {
      return { data: null, error: e as Error };
    }
  },

  async update(id: string, updates: Partial<LearnCourseRow>): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('learn_courses')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      return { error: error ? (error as Error) : null };
    } catch (e) {
      return { error: e as Error };
    }
  },

  async delete(id: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.from('learn_courses').delete().eq('id', id);
      return { error: error ? (error as Error) : null };
    } catch (e) {
      return { error: e as Error };
    }
  },
};

// ── Learn Articles (Learn page, employee-editable) ─────────

export const learnArticleService = {
  async getAll(limit = 50) {
    const { data, error } = await supabase
      .from('learn_articles')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },
  async getBySlug(slug: string) {
    const { data, error } = await supabase
      .from('learn_articles')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    return { data, error };
  },
  async create(item: {
    slug: string;
    title: string;
    subtitle?: string;
    category?: string;
    level?: string;
    read_time?: string;
    sections?: unknown[];
    created_by?: string;
  }) {
    const { data, error } = await supabase
      .from('learn_articles')
      .insert(item)
      .select()
      .single();
    return { data, error };
  },
  async update(id: string, updates: Record<string, unknown>) {
    return supabase.from('learn_articles').update(updates).eq('id', id);
  },
  async delete(id: string) {
    return supabase.from('learn_articles').delete().eq('id', id);
  },
};

// ── Culture Articles (Culture page, employee-editable) ──────

export const cultureArticleService = {
  async getAll(limit = 50) {
    const { data, error } = await supabase
      .from('culture_articles')
      .select('*')
      .eq('published', true)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },
  async getBySlug(slug: string) {
    const { data, error } = await supabase
      .from('culture_articles')
      .select('*')
      .eq('slug', slug)
      .single();
    return { data, error };
  },
  async create(item: { slug: string; title: string; subtitle?: string; body?: string; image_url?: string; category?: string; sort_order?: number; published?: boolean }) {
    const { data, error } = await supabase
      .from('culture_articles')
      .insert(item)
      .select()
      .single();
    return { data, error };
  },
  async update(id: string, updates: Record<string, unknown>) {
    return supabase.from('culture_articles').update(updates).eq('id', id);
  },
  async delete(id: string) {
    return supabase.from('culture_articles').delete().eq('id', id);
  },
};

// ── Editor Moments (Editor Picks feed, employee-editable) ───

export const editorMomentsService = {
  async getActive(limit = 10) {
    const { data, error } = await supabase
      .from('editor_moments')
      .select('*')
      .gte('expires_at', new Date().toISOString())
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },
  async create(item: { title: string; description?: string; curator_name?: string; curator_role?: string; theme?: string; post_ids?: string[]; expires_at: string }) {
    const { data, error } = await supabase
      .from('editor_moments')
      .insert(item)
      .select()
      .single();
    return { data, error };
  },
  async update(id: string, updates: Record<string, unknown>) {
    return supabase.from('editor_moments').update(updates).eq('id', id);
  },
  async delete(id: string) {
    return supabase.from('editor_moments').delete().eq('id', id);
  },
};

// ── Growth Programs (Grow Hub) ─────────────────────────────

export const growthProgramsService = {
  async getAll() {
    const { data, error } = await supabase.from('growth_programs').select('*').order('sort_order', { ascending: true });
    return { data: data || [], error };
  },
  async create(item: { title: string; link_to: string; description?: string; tag?: string; color?: string; icon?: string; sort_order?: number }) {
    const { data, error } = await supabase.from('growth_programs').insert(item).select().single();
    return { data, error };
  },
  async update(id: string, updates: Record<string, any>) {
    return supabase.from('growth_programs').update(updates).eq('id', id);
  },
  async delete(id: string) {
    return supabase.from('growth_programs').delete().eq('id', id);
  },
};
